// Translation dictionaries. Keep keys grouped by area for readability.
// Variables use {curlyBrace} placeholders.
//
// Adding a new string: add to BOTH `en` and `es`. If you forget `es`, the app
// silently falls back to `en` so nothing breaks visually.

export const dict = {
  en: {
    // --- Generic ---
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.delete": "Delete",
    "common.loading": "Loading…",
    "common.pleaseWait": "Please wait…",
    "common.welcomeBack": "Welcome back",
    "common.accountCreated": "Account created",
    "common.copied": "Copied",

    // --- Lang toggle ---
    "lang.toggle.aria": "Language",
    "lang.en": "EN",
    "lang.es": "ES",

    // --- Theme picker ---
    "theme.toggle.aria": "Theme",
    "theme.auto": "Auto (system)",
    "theme.orange": "Safety Orange",
    "theme.blueprint": "Blueprint Blue",
    "theme.forest": "Forest Green",
    "theme.steel": "Steel",
    "theme.highvis": "High-Vis Yellow",
    "theme.dark": "Jobsite Dark",
    "theme.status": "Theme: {name}",
    "theme.blurb": "Does not affect customer quotes — the quote preview, PDFs, and emails stay in the neutral document style.",

    // --- Layout / nav ---
    "nav.estimates": "Estimates",
    "nav.catalog": "Catalog",
    "nav.team": "Team",
    "nav.logout": "Log out",
    "nav.estimatorTag": "Estimator",
    "nav.role.owner": "Owner",
    "nav.role.estimator": "Estimator",

    // --- Login / Register ---
    "auth.dashboard": "Dashboard",
    "auth.sidingEstimator": "Siding Estimator",
    "auth.welcomeBack": "Welcome back",
    "auth.createAccount": "Create an account",
    "auth.signIn": "Sign in to your account",
    "auth.freeTagline": "Free siding estimator for our dealers",
    "auth.yourName": "Your name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.showPassword": "Show password",
    "auth.hidePassword": "Hide password",
    "auth.lockedOut": "Locked — this is not a wrong password",
    "auth.lockedOutHint": "Too many failed attempts from this connection. The wait above is the server's. A successful sign-in clears it immediately.",
    "auth.signInBtn": "Sign in",
    "auth.createBtn": "Create account",
    "auth.needAccount": "Need an account?",
    "auth.register": "Register",
    "auth.alreadyHaveOne": "Already have one?",
    "auth.newCompany": "New Company",
    "auth.joinTeammate": "Join Teammate",
    "auth.yourCompanyName": "Your company name",
    "auth.companyNamePlaceholder": "e.g. Smith & Sons Siding",
    "auth.accessCodeFrom": "Access code from {supplier}",
    "auth.accessCodePlaceholder": "ALSIDE-XXXXXX",
    "auth.teammateInviteCode": "Teammate invite code",
    "auth.invitePlaceholder": "ABCD1234",
    "auth.inviteHelp": "Joining an existing company? Get the 8-character code from a coworker.",
    "auth.providedBy": "Provided by {supplier}",

    // --- Your account (SEND-194 — self-service password change) ---
    "account.yourPassword": "Your Password",
    "account.blurb": "Change the password for your own sign-in. You need your current one, and every other device signed in as you will be signed out.",
    "account.current": "Current password",
    "account.new": "New password",
    "account.confirm": "Confirm new password",
    "account.mismatch": "The two new passwords do not match.",
    "account.changeBtn": "Change password",
    "account.changed": "Password changed",
    "account.sessionsNote": "Other devices will be signed out.",
    "account.lockedOut": "Locked — too many attempts",
    "account.seededNote": "Note: this is the account seeded from the server's ADMIN_PASSWORD value. Your new password now takes precedence and will not be overwritten on restart.",

    // --- Dashboard ---
    "dash.title": "Estimates",
    "dash.eyebrow": "Dashboard",
    "dash.exportCsv": "Export CSV",
    "dash.newEstimate": "New Estimate",
    "dash.search": "Search by customer, address, or estimate #",
    "dash.drafts": "Drafts",
    "dash.drafts.sub": "In progress",
    "dash.sent": "Sent",
    "dash.sent.sub": "{amount} pending",
    "dash.accepted": "Accepted",
    "dash.accepted.sub": "{amount} won",
    "dash.winRate": "Win Rate",
    "dash.winRate.sub": "{won} of {total} sent",
    "dash.filter.all": "All",
    "dash.filter.draft": "Draft",
    "dash.filter.sent": "Sent",
    "dash.filter.accepted": "Accepted",
    "dash.col.estNum": "Estimate #",
    "dash.col.customer": "Customer",
    "dash.col.address": "Address",
    "dash.col.sellPrice": "Sell Price",
    "dash.col.actions": "Actions",
    "dash.untitled": "Untitled",
    "dash.empty.title": "No estimates yet",
    "dash.empty.sub": "Create your first estimate to get going.",
    "dash.badge.accepted": "Accepted",
    "dash.badge.sent": "Sent",
    "dash.confirmDelete": "Delete this estimate?",
    "dash.deleted": "Estimate deleted",
    "dash.deleteTitle": "Delete",
    "dash.thisEstimate": "this estimate",
    "dash.itsLinked": "It's linked.",
    "dash.trashNote": "Deleting moves it to trash — recoverable for 30 days via Undo.",
    "dash.cancel": "Cancel",
    "dash.deleteConfirm": "Delete anyway",
    "dash.duplicated": "Estimate duplicated — customer fields cleared",
    "dash.duplicate.aria": "Duplicate",
    "dash.duplicate.title": "Duplicate this estimate",
    "dash.delete.aria": "Delete",

    // --- Catalog page ---
    "cat.title": "Price Catalog",
    "cat.eyebrow": "Settings",
    "cat.tier": "Tier: {name}",
    "cat.intro": "Material prices set by your supplier (read-only). Labor is yours to set — overrides save to your company only.",
    "cat.clearOverrides": "Clear Overrides",
    "cat.save": "Save Catalog",
    "cat.col.item": "Item",
    "cat.col.unit": "Unit",
    "cat.col.material": "Material $",
    "cat.col.labor": "Labor $",
    "cat.saved": "Catalog saved",
    "cat.resetConfirm": "Clear all your custom overrides and use the tier defaults?",
    "cat.resetDone": "Reset to tier defaults",

    // --- Estimate editor ---
    "est.jobInfo": "Job Information",
    "est.customer": "Customer",
    "est.address": "Address",
    "est.contactInfo": "Contact & Lead",
    "est.jobBilling": "Job & Billing Address",
    "est.email": "Email",
    "est.phoneCell": "Cell Phone",
    "est.phoneAlt": "Secondary Phone",
    "est.fax": "Fax",
    "est.contactMethod": "Preferred Contact",
    "est.contactMethod.cell": "Cell",
    "est.contactMethod.landline": "Landline",
    "est.contactMethod.email": "Email",
    "est.contactMethod.text": "Text",
    "est.company": "Company",
    "est.contactTitle": "Contact Title",
    "est.street": "Street",
    "est.city": "City",
    "est.state": "State",
    "est.zip": "ZIP",
    "est.billingSame": "Billing address same as job address",
    "est.billingAddress": "Billing Address",
    "est.leadSource": "Lead Source",
    "est.leadSource.referral": "Referral",
    "est.leadSource.repeat_customer": "Repeat Customer",
    "est.leadSource.web": "Web",
    "est.leadSource.social": "Social Media",
    "est.leadSource.yard_sign": "Yard Sign",
    "est.leadSource.truck_wrap": "Truck Wrap",
    "est.leadSource.home_show": "Home Show",
    "est.leadSource.supplier": "Supplier",
    "est.leadSource.door_knock": "Door Knock",
    "est.leadSource.other": "Other",
    "est.leadSourceDetail": "Detail",
    "est.contactHint": "Add email to send quotes",
    "est.exampleLead": "e.g.",
    "est.warnEmail": "Doesn't look like a valid email address",
    "est.warnPhone": "Doesn't look like a valid phone number — expected 10 digits, e.g. (412) 555-0100",
    "est.warnZip": "ZIP should be 5 digits, or ZIP+4",
    "quote.emailWillSave": "This email will be saved to the estimate.",
    "est.estimateNum": "Estimate #",
    "est.date": "Date",
    "est.estimator": "Estimator",
    "est.scope": "Scope of Work / Notes",
    "est.colors": "Material Colors",
    "est.color.siding": "Siding Color",
    "est.color.lpSiding": "LP Siding Color",
    "est.color.trim": "Trim Color",
    "est.color.ascend": "Ascend Color",
    "est.color.shake": "Shake Color (Pelican Bay)",
    "est.color.boardBatten": "Board & Batten Color",
    "est.color.accessories": "Accessories Color",
    "est.color.outsideCorner": "Outside Corner Color",
    "est.color.soffitFascia": "Soffit / Fascia Color",
    "est.color.windowWrap": "Window Wrap Color",
    "est.color.gutter": "Gutter / Downspout Color",
    "home.pickerTag": "Workspace",
    "home.pickerTitle": "Choose what you’re quoting",
    "home.pickerSubtitle": "Pick a quote family to enter the right workspace. Each one keeps its own list of estimates.",
    "home.workspaceTag": "Workspace",
    "home.issGroupTitle": "ISS Quotes",
    "home.issGroupDesc": "Single-tier ISS price book. Siding, windows, and new construction estimates with combined material + labor pricing.",
    "home.issGroupCta": "Open ISS Quotes →",
    "home.contractorGroupTitle": "Contractor Quotes",
    "home.contractorGroupDesc": "Multi-tier contractor pricing. Vero windows, Vinyl + Ascend siding, and LP SmartSide — each with its own catalog and labor rules.",
    "home.contractorGroupCta": "Open Contractor Quotes →",
    "home.back": "← Back",
    "home.iss.title": "ISS Quotes",
    "home.iss.subtitle": "Pick which kind of ISS quote you’re building.",
    "home.contractor.title": "Contractor Quotes",
    "home.contractor.subtitle": "Pick which contractor catalog this quote will use.",
    "home.issTitle": "ISS Remolding Siding Quotes ASCEND/Vinyl",
    "home.issDesc": "Single-tier siding estimator using the 2026 ISS price book. One combined material + labor price per line, 9 standard sections.",
    "home.issCta": "Open ISS Siding →",
    "home.issWindowsTitle": "ISS Replacement Window Quotes",
    "home.issWindowsDesc": "Vero-style windows on the ISS price book. Mirrors the current window flow with combined material + labor pricing.",
    "home.issWindowsCta": "Open ISS Windows →",
    "home.issNewConTitle": "ISS New Construction Siding Quotes",
    "home.issNewConDesc": "New-construction siding line items. Pricing sheet not loaded yet — this workspace will open once the catalog is provided.",
    "home.issNewConCta": "Coming Soon",
    "home.sidingTitle": "Vinyl + Ascend Siding Quotes",
    "home.sidingDesc": "Vinyl, Ascend Composite, soffit, gutters, accessories. Multi-tab estimator with HOVER auto-fill, color palettes, and material lists.",
    "home.sidingCta": "Open Vinyl + Ascend →",
    "home.windowsTitle": "Window Quotes",
    "home.windowsDesc": "Vero windows + sliding glass doors with install labor, upgrade options, lead-safe practices, and per-window color selection.",
    "home.windowsCta": "Open Window Quotes →",
    "home.underConstruction": "Under Construction",
    "home.lpTitle": "LP SmartSide Quotes",
    "home.lpDesc": "LP SmartSide — strand lap, panels, trim, and accessories. Dedicated workspace with per-tier margin pricing and zero labor by default.",
    "home.lpCta": "Open LP SmartSide →",
    "home.cardSidingTitle": "Siding Quotes",
    "home.cardWindowsTitle": "ISS Window Quotes",
    "est.colors.windows": "Window Colors",
    "est.color.windowFrame": "Frame Color",
    "est.color.windowInterior": "Interior Color",
    "est.color.windowExterior": "Exterior Color",
    "est.color.placeholder": "e.g. Storm Gray",
    "est.sync.headline": "Catalog prices have been updated",
    "est.sync.subline": "{n} item(s) on this draft have changed prices since you last saved",
    "est.sync.dismiss": "Dismiss",
    "est.sync.review": "Review & Sync",
    "est.sync.modal.title": "Sync to Latest Catalog",
    "est.sync.modal.subtitle": "Review {n} change(s) below — current totals will update once applied",
    "est.sync.col.item": "Item",
    "est.sync.col.qty": "Qty",
    "est.sync.col.matOld": "Mat (old)",
    "est.sync.col.matNew": "Mat (new)",
    "est.sync.col.labOld": "Lab (old)",
    "est.sync.col.labNew": "Lab (new)",
    "est.sync.cancel": "Cancel",
    "est.sync.applyButton": "Apply {n} & Save",
    "est.sync.applying": "Applying…",
    "est.sync.toast": "Synced {n} item(s) to current catalog prices",
    "est.wasteFactor": "Waste Factor",
    "est.wasteSuffix": "% extra material",
    "est.wasteHint": "Applies to Vinyl Siding + Ascend Composite Lap/B&B + LP SmartSide Lap (38 Series) — no waste is baked into the formula; this field is the only waste applied. Default follows the siding family — Lap 10% · Board & Batten 30% — one visible, editable number.",
    "est.overhang": "Eave Overhang",
    "est.overhangHint": "Drives the soffit piece count (default 12\")",
    "est.salesTax": "Sales Tax",
    "est.applyTaxOnMaterial": "Apply tax on material",
    "est.profit": "Profit",
    "est.margin": "Margin of sale",
    "est.markup": "Markup on cost",
    "est.marginSuffix": "% of your sale price kept as profit — your number (sale = cost ÷ (1 − %))",
    "est.markupSuffix": "% added on top of your cost — your number (sale = cost × (1 + %))",
    "est.jobPhotos": "Job Photos",
    "est.addPhoto": "Add",
    "est.noPhotos": "No photos yet.",
    "est.photoAdded": "Photo added",
    "est.summary": "Summary",
    "est.sum.material": "Material",
    "est.sum.waste": "Cut waste inside Material ({pct}% of Material)",
    "est.sum.tax": "Tax ({pct}%)",
    "est.sum.labor": "Labor",
    "est.sum.baseCost": "Base Cost",
    "est.sum.sell": "Your price to homeowner ({pct}% {mode})",
    "lp.mat.title": "Material List (AI-read)",
    "lp.fv.title": "Field verify — AI-read locations awaiting confirmation",
    "lp.fv.note": "Amber locations are already included in stick counts (presence guarantee). Confirm each on site — verifying never changes quantities; relocating moves the detected feature to its true wall; \u201cNot present\u201d removes it from counts (revertible).",
    "lp.fv.verify": "Confirmed in field",
    "lp.fv.wrongWall": "Wrong wall",
    "lp.fv.notPresent": "Not present",
    "lp.fv.pickWall": "Correct wall\u2026",
    "lp.fv.relocated": "Relocated",
    "lp.fv.removed": "Removed",
    "lp.fv.wall": "wall",
    "lp.fv.verified": "Confirmed — in field",
    "lp.fv.tape.title": "Tape it on site — optional quick entry",
    "lp.fv.tape.note": "This feature still carries assumed dimensions (render-only, footnoted on the customer PDF). Enter what you tape — entered values become Confirmed (revertible, re-derives quantities). Skipping keeps the assumed state.",
    "lp.fv.tape.height": "Height",
    "lp.fv.tape.depth": "Depth",
    "lp.fv.tape.save": "Save measurements",
    "lp.fv.tape.ft": "ft",
    "lp.or.title": "Confirm openings — pre-derivation review",
    "lp.or.note": "One-tap ratification of AI-read openings. Confirming promotes an opening to Confirmed standing; correcting a type re-derives the counts; marking one \u201cNot present\u201d removes it from all counts and trim math (revertible). Skippable — AI-read flags persist.",
    "lp.or.reviewed": "reviewed",
    "lp.or.unconfirmed": "AI-read",
    "lp.or.confirm": "Confirm",
    "lp.or.confirmed": "Confirmed",
    "lp.or.corrected": "Corrected",
    "lp.or.correctedShort": "Corrected",
    "lp.or.wrongType": "Wrong type",
    "lp.or.pickType": "Actual type…",
    "lp.or.viewPhoto": "View source photo",
    "lp.or.notPresent": "Not present",
    "lp.or.removed": "Removed",
    "lp.or.removedShort": "removed",
    "lp.or.removeWarn": "This opening feeds derived lines —",
    "lp.or.removeAnyway": "Remove anyway",
    "lp.or.cancel": "Cancel",
    "lp.mat.colors": "Colors",
    "lp.mat.allComponents": "All components",
    "lp.mat.edit": "Edit list",
    "lp.mat.lock": "Done editing",
    "lp.mat.readonly": "Read-only — click Edit list to substitute products. Substitutions are session-scoped by design: the list always re-derives from the latest geometry.",
    "lp.mat.pending": "pricing pending",
    "lp.mat.total": "Materials total",
    "lp.mat.noRun": "Run AI Measure first — the material list derives from confirmed measurements.",
    "lp.mat.substituted": "substituted from",
    "lp.mat.provenance": "How this number was derived",
    "est.customerQuote": "Customer Quote",
    "est.materialList": "Material List",
    "est.tabOption": "{tab} option",
    "est.print": "Print",
    "est.exportCsv": "Export CSV",
    "est.barLabel": "Estimate",
    "est.untitled": "Untitled",
    "est.bar.base": "Base",
    "est.bar.derivedUnapplied": "Derived — not yet applied",
    "est.bar.sell": "Sell",
    "est.bar.profit": "Profit",
    "est.loading": "Loading estimate…",
    "est.col.item": "Item",
    "est.col.unit": "Unit",
    "est.note.add": "+ note",
    "est.note.chip": "note",
    "est.note.prints": "Prints on the material list",
    "est.note.placeholder": "Contractor note for this row — prints on the material list",
    "est.col.mat": "Mat $",
    "est.col.qty": "Qty",
    "est.col.lab": "Lab $",
    "est.col.total": "Total",
    "est.itemsBadge": "{n} items",
    "est.customDesc": "Custom Description",
    "est.customDescPlaceholder": "Custom item description",
    "est.addCustomLine": "Add custom line",
    "est.removeMisc": "Remove misc row",

    // --- Quote modal ---
    "quote.subject": "Subject",
    "quote.personalNote": "Personal note (appears above the quote)",
    "quote.personalNoteHelp": "The customer will see this note first, then the estimate below.",
    "quote.recipientPlaceholder": "customer@example.com",
    "quote.emailBtn": "Email",
    "quote.sending": "Sending…",
    "quote.downloadPdf": "Download PDF",
    "quote.emailNotConfigured": "Email service not configured. Add RESEND_API_KEY in backend/.env to enable sending.",
    "quote.langPicker": "Send in:",
    "quote.sentToast": "Email sent",
    "quote.docSubtitle": "Estimate · Quote",
    "quote.signature": "Customer Signature",
    "quote.dateSigned": "Date",
    "quote.printBlocked": "PRINT-BLOCKED: {n} item(s) — clear the readiness checklist before this quote reaches a homeowner.",
    "quote.printBlockedMore": "… and {n} more (see Readiness on the estimate page)",
    "quote.printBlockedTooltip": "PRINT-BLOCKED — clear the readiness checklist first",
    "gates.finalJob": "Final-job surface",
    "gates.badge.quote": "Ready to quote?",
    "gates.badge.order": "Ready to order?",
    "gates.badge.clear": "CLEAR",
    "gates.badge.blocked": "BLOCKED",
    "gates.quoteBlockers": "Not ready to quote — these block email · Accept page · PDF · freeze · QR:",
    "gates.orderItems": "Ready to order? — taped fields close here and supersede derived values:",
    "gates.releaseMaterials": "Release materials",
    "gates.releasing": "Releasing…",
    "gates.orderBlocked": "Not ready to order",
    "gates.releasedBy": "Materials released by {by} @ {at}",
    "readiness.title": "Estimate readiness",
    "readiness.refresh": "Refresh",
    "readiness.checking": "Checking…",
    "readiness.unavailable": "Readiness unavailable.",
    "readiness.allClear": "All clear — nothing stands between you and a real number.",
    "readiness.openCount": "{n} open item(s)",
    "readiness.footer": "Items marked blocking stop the customer quote — the rest are advisories.",
    "readiness.kind.pending_price": "Pending prices",
    "readiness.kind.open_flag": "Open flags",
    "readiness.kind.field_verify": "Field-verify",
    "readiness.kind.unpriced_row": "Unpriced money rows",
    "readiness.kind.labor_pending_row": "Labor pending — contractor to price",
    "readiness.kind.labor_pending": "Labor undecided",
    "readiness.kind.qty_pending": "Quantity pending",
    "readiness.kind.family_conflict": "Family conflict",
    "readiness.kind.quote_gate": "Quote readiness",
    "est.readinessBtn": "Readiness",
    "est.derivedChip": "derived",
    "jip.pending.title": "Pending — not applied yet ({n})",
    "jip.pending.blueprint": "Blueprint",
    "jip.pending.photo": "Photo",
    "jip.pending.row": "{door} run {date}{sqft} — never prices until you review & apply it",
    "jip.pending.sqft": " — {n} ft² siding",
    "jip.pending.more": "…and {n} more",
    "jip.collapse": "Collapse",
    "jip.edit": "Edit",
    "jip.expand": "Expand",
    "jip.tile.blueprints": "Blueprints",
    "jip.tile.aiPhoto": "Draw on Photos",
    "oneDoor.listTitle": "Photos on this job — you pick who reads each one",
    "oneDoor.capture": "Take photos",
    "oneDoor.aiMayRead": "AI may read",
    "oneDoor.handOnly": "Hand only",
    "oneDoor.clear": "Clear",
    "oneDoor.noChoice": "No choice",
    "oneDoor.deleteNo": "No",
    "oneDoor.deleteYes": "Yes, delete",
    "oneDoor.faceNone": "no face tagged",
    "oneDoor.stateAi": "AI may read this photo",
    "oneDoor.stateHand": "Hand only — no AI read",
    "oneDoor.stateNone": "No choice recorded",
    "intake.importHover": "Import HOVER",
    "intake.reading": "Reading…",
    "intake.readBlueprints": "Draw on Blueprints",
    "intake.aiMeasure": "Draw on Photos",
    "intake.aiMeasureResume": "Draw on Photos (Resume)",
    "hov.cutNote": "LP estimate — composition lines derive through the LP engine on Apply (THE CUT). This door merges no vinyl/Ascend/windows lines.",
    "hov.applyLp": "Apply & Derive LP List",
    "hov.restore": "Restore HOVER Lines",
    "bp.restore": "Restore Blueprint Read",
    "bp.restoreTitle": "Re-open the most recent blueprint read — {pg} pages, {min} min ago. Display only, no new read.",
    "err.pdf.generic": "Couldn't create the PDF — check your connection and try again.",
    "err.gate.quote": "Can't send or print the quote yet — a few things still need a price or a decision:",
    "err.gate.materials": "Can't print the material list yet — a few readings still need to be resolved:",
    "err.gate.order": "Can't release these materials yet — finish the field checks first:",
    "err.gate.action": "Take care of these, then try again",
    "err.gate.more": "+{n} more",
    "err.spec.lines": "Your setting saved, but the material lines didn't update — press RE-DERIVE to bring them current.",
    "hov.print": "Print",
    // ---- Blueprint Read-Back Card (verification, display-only) ----
    "bp.rb.title": "Read-Back — what the app read",
    "bp.rb.readonly": "Display only — reads the run, changes nothing",
    "bp.rb.run": "run",
    "bp.rb.reap": "reaped at {when} unless applied/archived",
    "bp.rb.archived": "archived — never reaped",
    "bp.rb.sheets": "Synthetic elevations (Phase 1)",
    "bp.rb.sheets.note": "rendered from the blueprint model — positions schematic, never the architectural sheet",
    "bp.rb.planes": "Roof-plane census",
    "bp.rb.plane": "Plane",
    "bp.rb.eave": "Eave LF",
    "bp.rb.rake": "Rake LF",
    "bp.rb.gableEnds": "Gable ends",
    "bp.rb.porchSqft": "Porch ft²",
    "bp.rb.totals": "Plane totals",
    "bp.rb.gableBlind": "RAKE 0 on \"{plane}\" — gable-blind plane. If the elevation shows this wing under its own gable, the read missed its rakes. Verify.",
    "bp.rb.noPlanes": "NO ROOF PLANES READ — four-wall rectangle only. Every wing eave and rake (garage, porch, cross-gable) is invisible to this read.",
    "bp.rb.noGarage": "NO GARAGE PLANE — the plans show a garage but no garage roof was read (or it came back gable-blind). The garage eave/rake runs are missing.",
    "bp.rb.corners": "Corner ledger",
    "bp.rb.outside": "Outside",
    "bp.rb.inside": "Inside",
    "bp.rb.invariant": "Walk invariant (out − in = 4)",
    "bp.rb.pass": "PASS",
    "bp.rb.fail": "FAIL — re-walk the footprint",
    "bp.rb.basis.per_corner": "Corner LF basis: per-corner heights, summed",
    "bp.rb.basis.averaged": "Corner LF basis: count × AVERAGE height — verify. Averaging blurs tall 2-story corners with short wing corners.",
    "bp.rb.basis.missing": "No corner LF read",
    "bp.rb.wing": "Footprint {fp} ft² exceeds the wall rectangle {rect} ft² — a projecting wing exists. A rectangle-only corner walk missed its corners.",
    "bp.rb.porch": "Porch",
    "bp.rb.porch.plane_read": "Porch plane read — ceiling {sqft} ft²",
    "bp.rb.porch.plane_without_ceiling": "Porch plane WITHOUT a ceiling figure — verify the floor plan",
    "bp.rb.porch.phantom_ceiling": "PHANTOM? {sqft} ft² porch ceiling with NO porch plane — a tag over nothing. Verify against the floor plan.",
    "bp.rb.porch.absent": "No porch read — verify the floor plan (covered porches are commonly dropped)",
    "bp.rb.cornerHeights": "Per-corner heights",
    "bp.rb.heights.read": "Per-corner heights read — each corner takes its taller wall, run to the eave. These govern the corner line.",
    "bp.rb.heights.undim": "{n} corner(s) carry no printed dimension — held at 1 stick, flag stands.",
    "bp.rb.gutterRuns": "Gutter run inventory",
    "bp.rb.gutterRuns.governs": "These runs govern the gutter line — the eave plane-sum stays the soffit figure.",
    "bp.rb.gutterRuns.none": "No run inventory on this read — the gutter line falls back to the eave plane-sum. A re-read carries the runs.",
    "bp.rb.source": "Source file",
    "bp.rb.source.native": "Original file kept. Printed text found on {n} of {m} pages — the dimensions exist as real text on this file, not just pixels.",
    "bp.rb.source.mixed": "Original file kept. Printed text on {n} of {m} pages — the rest are picture-only, so their numbers are vision reads.",
    "bp.rb.source.scan": "Original file kept. This file carries no text layer — the pages are pictures, so every number on this card is a vision read.",
    "bp.rb.source.images": "Photo sheets kept exactly as uploaded. Every number on this card is a vision read.",
    "bp.rb.consistency": "Consistency check",
    "bp.rb.consistency.clean": "No contradictions found between the read's own numbers.",
    "bp.rb.consistency.corner_taller_than_wall": "{n} corner height(s) read {h}' (corner ledger) while the wall table reads {wall}' — two AI-read sources disagree about the same house. This card resolves toward NEITHER: both are vision reads. A taped or contractor-entered height outranks both.",
    "bp.rb.consistency.corner_lf_not_sum": "Corner LF prints {lf} but its own per-corner heights sum to {sum}. A stated total must equal the sum of its parts.",
    "bp.rb.consistency.corner_walk_conflict": "Two AI reads disagree about the corner walk — the primary read {p_out} outside / {p_in} inside, the roof pass {r_out} outside / {r_in} inside. THE PRIMARY STANDS; neither is adopted as truth. Mark up the elevations and count — a taped or contractor count outranks both.",
    "bp.rb.consistency.eave_rake_orientation": "The eave figure ({eaves} LF) matches the GABLE wall pair ({gsum} LF) better than the eave wall pair ({esum} LF). On a gable roof, eaves and rakes sit on opposite wall pairs — this read may carry the house rotated 90°. Check which walls hold the gutters before ordering.",
    "bp.rb.consistency.opposing_walls_disagree": "Opposing walls disagree — {pair}: {a}' vs {b}'. A rectangular body has equal opposing walls; a difference here usually means a projecting wing or a misread. The box-model check now uses the SHORTER of the pair so a hidden wing can still be caught. Mark which wall carries the jog.",
    "bp.rb.consistency.gable_census_mismatch": "The roof-plane census counts {planes} gable ends but only {walls} wall(s) carry a gable triangle — a gable has no wall to live on.",
    "bp.rb.consistency.box_model": "Front=back and left=right EXACTLY, yet the footprint proves a projecting wing. The wing's walls are missing from the read — this looks like a box-model guess, not a read.",
    "bp.rb.consistency.run_exceeds_facade": "The {label} gutter run prints {run}' but the {label} wall is only {wall}' wide — a continuous run can't outrun its facade.",
    "bp.rb.consistency.wall_segments_mismatch": "The {label} wall's height sections sum to {sum}' of width against a {width}' wall — the section walk doesn't close. Siding math held the rectangle instead of consuming a broken walk.",
    "bp.rb.consistency.footprint_missing": "No printed footprint captured this read — the wing check (footprint vs wall rectangle) CANNOT run. Its absence is named, not silent.",
    "bp.rb.consistency.porch_run_vs_width": "The porch gutter run prints {run}' while the porch's printed width is {width}' — one porch, two answers. Both are reads; neither governs until verified.",
    "bp.rb.consistency.porch_dims_vs_area": "Porch prints {w}' × {d}' = {product} ft², but the area table says {area} ft² — the stated dimensions don't reproduce the stated area. A dimension is missing inches somewhere.",
    "bp.rb.consistency.window_size_parse_mismatch": "Window {mark}: the schedule string \"{printed}\" parses to {parsed} in, but the read carries {carried} in — the transcription contradicts itself.",
    "bp.rb.consistency.mark_size_conflict": "Mark {mark} carries two different printed sizes — {sizes}. Marks may have merged across schedule sheets — check each sheet's own row for this mark.",
    "bp.rb.consistency.count_column_mismatch": "Mark {mark}: the schedule's COUNT cells say {cells} (sum {summed}), but the read carries {carried}. The count column governs — a quantity that ignores it is symbol-counting.",
    "bp.rb.consistency.schedule_count_unread": "{kind} mark {mark}: the schedule row REFUSES its count — {reason}. Nothing is estimated; the mark contributes no quantity until the cell reads or a human supplies the count.",
    "bp.rb.consistency.schedule_row_recovered": "Door {mark} recovered from the printed schedule row on sheet {page} ({type}) — the model's read missed it. Only what the row prints is carried.",
    "bp.rb.consistency.openings_unplaced": "{count} schedule-derived opening(s) ride the UNPLACED bucket — a schedule gives size and count and is silent on location by design. No face is assigned until a placement read exists. The opening deduction lands at AGGREGATE — not attributed per face.",
    "bp.rb.consistency.dims_unattributed_quantity_refused": "{count} dimension(s) located but UNATTRIBUTED — {dims}. One printed figure is claimed by more than one face, so ownership is not established: the figure is shown here and REFUSED by every quantity (wall and gable area, starter, perimeter, eaves, rakes). Establish which face the dimension belongs to before pricing.",
    "bp.rb.consistency.faces_refused": "{count} face(s) REFUSED — {faces}. Their height or width could not be established from their own elevation drawings; each contributes NOTHING until taped or a placement read lands. Silence here would mean nothing is wrong — it is not.",
    "bp.rb.consistency.opening_sizes_refused": "{count} opening mark(s) refused their size — {marks}. Each is counted but contributes 0 ft² to any deduction until its printed size reads or a human supplies it.",
    "bp.rb.consistency.deduction_refused": "The openings deduction REFUSED — {rows} opening row(s) read, but {count} face(s) refused ({faces}). An opening may only deduct from a gross that includes the face it sits on, and no placement exists to scope it. Nothing was deducted.",
    "bp.rb.consistency.page_rotation_normalized": "{count} page(s) rotated in the raster and were normalized upright before the read — pages {pages}. Detection is the upright-share collapse across the three OCR passes; the cut comes from the observed gap, not a round number.",
    "bp.rb.consistency.lf_lane_refused": "{count} computed LF lane(s) REFUSED — {lanes}. These arrive as bare model arithmetic over the wall dimensions, and the dimensions they stand on were nulled by the evidence guard — a computed total may not outlive its inputs. The reason is named in brackets beside each lane. Tape or enter the runs by hand.",
    "bp.rb.consistency.below_grade_unread": "A BELOW-GRADE level is drawn ({pages}) — this read has no below-grade / walkout path. Any walkout siding is NOT included and nothing shown counts it. Flag the walkout by hand until a walkout ruling lands.",
    "bp.rb.consistency.material_claims_unconfirmed": "{count} material claim(s) awaiting human confirmation — {claims}. Material callouts are the model's reading, never verified against the drawing; they route square footage between siding profiles (and a gated % claim was reverted to 100). Confirm or correct each before the takeoff is priced.",
    "bp.rb.consistency.page_rotation_indeterminate": "{count} page(s) fell in the rotation gap — pages {pages}. NEVER normalized on a guess: each stands as rendered and is read both ways; a disagreement between the two readings would print here.",
    "sp.defaulted": "PROFILE DEFAULTED — {name} was assumed, not chosen ({qty} SQ ride on it). Tap to choose the profile; the quantity moves with your choice.",
    "sp.chosen": "Profile — {name} (your choice). Tap to change; the quantity moves with it.",
    "sp.stale": "Your profile choice ({name}) no longer carries the quantity — a re-apply reset it to {carrier}. Tap to re-choose.",
    "sp.choose": "Choose siding profile",
    "sp.pickTitle": "Choose the siding profile — the current quantity moves to your pick",
    "sp.none": "No vinyl siding profiles in the catalog for this estimate.",
    "bp.rb.consistency.door_size_parse_mismatch": "Door {mark}: the schedule prints \"{printed}\" ({parsed} in), but the read carries {carried} in — the printed size governs; a human should check this row.",
    "bp.rb.rail.ocr_quote_miss": "A machine text-read of the page cannot find print quoted by the read — {text}. Two independent reads of the same pixels disagree — one of them is wrong. Check the sheet; neither wins by default.",
    "bp.va.ocrloc": "LOCATED BY MACHINE TEXT-READ — the box is where a local text-read (OCR) finds the quoted print on the page image. The VALUE still comes from the read, never from the locator.",
    "bp.rb.consistency.wall_segment_undimensioned": "The {label} wall's \"{section}\" section carries NO printed height — the drawing holds no number for it. Flagged, never guessed; the wall's siding held the rectangle until a real dimension arrives.",
    "bp.rb.consistency.footprint_is_total_finished": "The footprint reads {fp} ft² — equal to the TOTAL FINISHED figure ({tf} ft²) while a second storey exists. Storeys summed is a living area, not a footprint. Labelled quantities read as labelled.",
    "bp.rb.rail": "Honesty flags",
    "bp.rb.rail.pitch": "Pitch (printed): {text}",
    "bp.rb.rail.no_pitch": "NO PITCH printed — slope assumed. Verify against the elevations.",
    "bp.rb.rail.scale_confidence": "Scale confidence: {text}",
    "bp.rb.rail.roof_pass_merge": "Roof-pass merge accepted: {text}",
    "bp.rb.rail.roof_pass_rejected": "Roof-pass overwrite REFUSED — {text}. An evidenced value is never replaced by an unevidenced one; the primary read's quoted figure stands.",
    "bp.rb.rail.pages_truncated": "THIS PLAN SET HAS MORE PAGES THAN THE APP READ — {text} (pages in the set → pages read). Dropped pages are invisible to the read, the locator, and every census. Split the set or raise the page cap before trusting this takeoff.",
    "bp.rb.rail.read_notes": "Read notes — {text}",
    "bp.rb.rail.overhang_printed": "Eave overhang read off the drawings: {text}\".",
    "bp.rb.rail.overhang_default": "Eave overhang NOT dimensioned on the drawings — the app is holding its default. Verify in field before ordering soffit.",
    "bp.rb.rail.overhang_by_plane": "Overhang printed per plane — {text}.",
    "bp.rb.rail.overhang_missing_on_planes": "Overhang NOT dimensioned on plane(s) {text} — the read did not resolve an eave depth here. Name where you taped, or verify in field before ordering.",
    "bp.rb.rail.pitch_missing_on_planes": "Pitch NOT printed on plane(s) {text} — the app does NOT inherit the main body pitch here. Gable rise stays null until a pitch is read or taped.",
    "bp.rb.rail.pitch_varies_by_plane": "Pitch varies by plane — {text}. A wing at a steeper pitch than the main body computes its own rise; the sheet does not average.",
    "bp.rb.rail.wall_height_by_plane": "Wall height printed per plane — {text}. Used verbatim; additions to reach the sided height (plates + foundation) are named separately, never silently added.",
    "bp.rb.rail.dims_unverified": "Dims MARKED UNVERIFIED — the quoted string may be real but we could not confirm it near its feature: {text}. Kept for review; not fed to money. Confirm in field.",
    "bp.rb.rail.dims_fabricated": "Dims KILLED — the quoted string does not appear on the drawings in any orientation: {text}. Each path is badged [strong] (dense OCR + no hit — real fabrication) or [weak] (sparse OCR — the miss may be OCR's fault, not the model's). Dropped either way.",
    "bp.rb.rail.dims_misread": "Dims KILLED as MISREADS — the quoted string is absent from the page but a real OCR run sits one character-edit away: {text}. Diagnosed as a transcription typo, not an invention. Value nulled for money; real printed string named for the field.",
    "bp.rb.rail.dims_shared_source": "Dim(s) sharing evidence with another path — {text}. A shared printed quote is a REAL, located dimension and is OFTEN correct (58'-0 overall genuinely is both front and back width), so it is SHOWN here with every consumer named. AMENDED send-127: where one quote is claimed by two different faces for the same field, ownership is not established and the figure no longer feeds any quantity — display only.",
    "bp.rb.rail.attribution_corroboration": "CORROBORATION — {text}. A located-but-unattributed WIDTH may feed a quantity only when a second independent read of the drawn line-work confirms it: RESOLVED on this face's own drawing, a wall-only figure (never a silhouette), no fence-margin warning, a clean scale quote, and a difference inside the registered 3.8% elevation noise floor. The PRINTED figure feeds the quantity — the drawn read confirms, it never measures. The difference is printed either way. Corroboration is a width instrument only: a height can never be corroborated this way, because the height is the read's own ruler.",
    "bp.rb.rail.dims_unattributed_quantity_refused": "UNATTRIBUTED — {text}. One printed figure is claimed by more than one face, so which face owns it is not established. Evidence-and-attribution-or-null: the figure is shown here and REFUSED by every quantity — wall and gable area, starter, perimeter, eaves, rakes, corner LF. Establish the owner before pricing.",
    "bp.rb.rail.dims_shared_source_conflict": "ATTRIBUTION CONFLICT — {text}. One quote feeds the SAME field on two different named features (two walls' widths, say). The value is kept — we cannot tell which consumer is wrong, and the standing rule is to REPORT the disagreement rather than resolve it (send-13).",
    "bp.rb.consistency.openings_exceed_wall_width": "Openings on the {wall} wall sum to {openings_sum_ft:g} ft — {excess_ft:g} ft more than the wall's own {wall_width_ft:g} ft. Cannot fit ({openings}). Verify wall attribution.",
    "bp.rb.rail.fascia_printed": "Fascia width read off the drawings: {text}\".",
    "bp.rb.rail.fascia_default": "Fascia width NOT dimensioned on the drawings — the app is holding its default. Verify in field before ordering coil.",
    "bp.rb.rail.interior_doors_dropped": "{text} interior door(s) from the floor plans were kept OFF the exterior count — doors drive J-channel and coil.",
    "bp.rb.rail.count_column_governed": "The printed COUNT column governs — carried quantities were replaced by the schedule's own cells: {text}. Symbols on a floor plan are never counted.",
    "bp.rb.rail.count_cells_unread": "The schedule prints counts, but no COUNT cell was read for mark(s) {text} — they contribute NOTHING until a human reads the cell. Flagged, never estimated.",
    "bp.rb.rail.count_cell_conflict": "One mark, one sheet, two different COUNT cells: {text}. The schedule cannot say both — a human resolves it against the sheet.",
    "bp.rb.rail.count_column_absent": "No printed COUNT column was read for the window schedule — quantities carry no printed source and are unverified.",
    "bp.rb.rail.mark_rows_merged": "Per-sheet schedule rows merged into one mark, counts summed across sheets: {text}. A per-sheet row is not a distinct mark.",
    "bp.rb.rail.mark_not_located": "A machine text-read cannot find these schedule rows' print anywhere on their sheets (all rotations checked): {text}. No quote locates — the row was fabricated and is DROPPED from the read.",
    "bp.rb.rail.mark_quote_miss": "A machine text-read cannot find print quoted on a schedule row — {text}. The quoted size is killed; its parse never reaches the takeoff. Check the sheet.",
    "bp.rb.rail.schedule_row_omitted": "The page prints schedule rows the read never carried — {text}. A missing row is silent and invisible; this is the omission instrument firing. Check the schedule and add what it holds.",
    "bp.rb.rail.interior_signal_machine": "A machine read of the product-code column says these door rows are INTERIOR — {text}. Dropped regardless of the read's own label; HOLLOW CORE cannot wear an exterior label.",
    "bp.rb.rail.skeleton_match": "Located on the whole-inch skeleton — {text}. The machine cannot read the stacked fraction glyphs; the whole-inch digits matched the sheet. The fractions themselves rest on the read's transcription.",
    "bp.rb.rail.dim_located_by_skeleton": "A wall dimension located on its fraction-stripped skeleton — {text}. The machine could not read the stacked fraction, so the whole-inch digits matched the sheet; the fractions rest on the read's transcription. An ambiguous or shared skeleton never locates.",
    "bp.rb.rail.mark_merge_suspected": "Two marks share one product code — {text}. A real schedule never prints that: the read merged sibling rows (the Mark B/C/D family). The trailing digits are the row's identity — check both rows against the sheet.",
    "bp.rb.rail.callout_omitted": "The elevations print a siding profile the read never carried — {text}. A missing profile is a silent miss on the takeoff; check the elevation callouts and add the accent.",
    "build.stale.msg": "This page is running an older build than the server (page {client} · server {server}). What you see can silently disagree with the backend until you refresh.",
    "build.stale.btn": "Refresh now",
    "server.waking.msg": "The server is restarting — reconnecting and retrying your last request. Nothing shown below is trustworthy until this banner clears.",
    "pe.empty": "No per-elevation breakdown on this measurement — profile accents hang on elevations. Photo and blueprint reads carry one; this read does not.",
    "bp.rb.rail.soffit_finish_printed": "Soffit finish stated on the drawings: {text}. Set the soffit steer to match — this is not a default.",
    "bp.rb.rail.soffit_finish_default": "No soffit finish annotation captured — vented-vs-solid is UNREAD. Check the elevations before steering the soffit split.",
    "bp.rb.rail.overhang_varies": "Overhang varies by location on this house — one number cannot cover it: {text}",
    "bp.rb.rail.dims_nulled_no_evidence": "Dropped by construction — these dimensions arrived WITHOUT a quoted printed string and were nulled (evidence-or-null, ruled 2026-08-08): {text}. The drawing may hold them; the read didn't prove it.",
    "bp.rb.stability": "Did two reads agree?",
    "bp.rb.stability.agreed": "TWO READS AGREED — stability, not correctness. Agreement means the read repeats; only a tape measurement or a human check against the printed dimension makes a number right.",
    "bp.rb.stability.mismatch": "READS DISAGREE — {name}: {a} vs {b}. The read is not stable here; a human has to resolve it.",
    "bp.rb.stability.abstained": "Both reads abstained on: {text} — stably unread. The drawing did not give these up; they still need a human.",
    "bp.rb.stability.notComparable": "Per-dimension compare unavailable — the earlier read predates the evidence register. Counts and totals above are still compared; the next rerun will compare dimension-by-dimension.",
    "bp.va.title": "Visual audit — where each number came from",
    "bp.va.approx": "APPROXIMATE LOCATION — vision-read box on a scan; the highlight is the model's estimate of where the print sits, not a measurement.",
    "bp.va.exact": "EXACT — located in the PDF's text layer.",
    "bp.va.noloc": "No location returned — the quoted string is the only anchor.",
    "bp.va.calc": "Arithmetic: {text}",
    "bp.va.page": "Sheet {n}",
    "bp.va.dropped": "NO SOURCE — arrived without a printed quote and was dropped by construction: {text}",
    "bp.va.unread": "Not read — no printed dimension resolved: {text}",
    "bp.va.none": "No evidenced dimensions on this read.",
    "bp.rb.rail.no_planes": "No roof planes in this read — plane-dependent lines fall to the four-wall rectangle",
    "hov.cancel": "Cancel",
    "hov.agree": "I Agree",
    "hov.qtyVerifyTitle": "Quantity Verification Required",
    "hov.qtyVerifyBody": "You are responsible for verifying all quantities before submitting this report.",
    "hov.openingsTitle": "Vero Window Openings — Style Guess",
    "hov.openingsTap": "Window openings ({n}) — tap to review styles",
    "hov.openingsExpanded": "({n} opening(s) · edit any style before applying)",
    "hov.needsReview": "{n} need review",
    "hov.sanity": "Sanity check · {n} warning(s)",
    "hov.tapToReview": "— tap to review",
    "sr.integralJ.title": "Window J-channel",
    "sr.integralJ.label": "Windows carry integral J-channel",
    "sr.integralJ.note": "Yes = no separate window J, no window caulk, no window wrap, no capping — each touched line prints its own note.",
    "sr.wallHeights.title": "B&B wall heights (taped) — ORDER stage, not needed to quote",
    "sr.wallHeights.taped": "Taped — {n} ft total feeds the batten height term.",
    "sr.wallHeights.reopen": "Reopen",
    "sr.wallHeights.placeholder": "heights ft, e.g. 9, 9, 18.5, 9",
    "sr.wallHeights.tapeIt": "Tape it",
    "sr.wallHeights.note": "Quote sells from the derived (HOVER-SCHEDULE) height — enter taped heights at the house before material order. Taped supersedes derived, reversible; battens re-derive live",
    "sr.wasteBaked": "Baked into line qty on import — change % to recompute",
    "sr.recomputeWaste": "Recompute waste on existing lines",
    "sr.soffitType": "LP Soffit type",
    "sr.soffit.mix": "Mix — Vented on eaves, Closed on rakes (default)",
    "sr.soffit.vented": "Vented — all soffit qty as Vented (38 Series Vented)",
    "sr.soffit.closed": "Closed — all soffit qty as Closed (38 Series Closed)",
    "sr.soffit.note": "Applied on HOVER / Blueprint import — collapses or splits the two soffit lines automatically",
    "sr.tradeSpecs": "Trade specs",
    "sr.rederive": "Re-derive material list",
    "sr.fasciaWidth440": "Fascia width — 440 Series",
    "sr.fasciaWidth": "Fascia width",
    "sr.default": "(default)",
    "sr.fascia.noteLp": "Contractor call-out — no derivation. Picks the 440 board width; the material list prints the width on the line. Re-derives live.",
    "sr.fascia.noteCoil": "Governs .019 coil coverage: ≤10\" the 24\" roll rips in half for 100 LF/roll; over 10\" one roll covers 50 LF. Re-derives live.",
    "sr.battenSpacing": "Batten spacing",
    "sr.batten.note": "Every 48\" seam lands on a batten. The 190 Series line note names the spacing and the piece delta vs the 12\" default when it moves.",
    "sr.panelSize": "Panel size — 38 Series B&B",
    "sr.panel.opt410": "4' × 10' (default, 40 ft²)",
    "sr.panel.opt48": "4' × 8' (32 ft²)",
    "sr.panel.note": "Changes the panel COUNT and the SKU — 4×8 coverage is 32 ft². Re-derives live.",
    "sr.wrapWidth": "Wrap trim width — 540 Series",
    "sr.wrap.note": "Changes ONLY the 540 SKU name (wrap + ISC + frieze scope) — counts stay whole-stick ÷16. Re-derives live.",
    "sr.shakeReveal": "Shake reveal",
    "sr.shake.note": "Bounded 7\"–10\", default 7\" — LP install instructions: \"540 Series Trim is recommended when the shake reveal selected ranges between a maximum of 10 inches to a minimum of 7 inches\". Coverage = 4' × reveal ÷ 12 (panel max 9-7/8\"); sealed 15% shake waste applies on top.",
    "sr.in": "in",
    "sr.sellMargin": "Sell = Base ÷ (1 − {pct}%) =",
    "sr.sellMarkup": "Sell = Base × (1+{pct}%) =",
    "fv.title": "Field Verify — AI-read numbers · tape them to confirm",
    "fv.chip.taped": "TAPED ×{n}",
    "fv.chip.notTaped": "NOT TAPED — AI READ",
    "fv.door.blueprint": "View Source Blueprints →",
    "fv.door.photo": "View Source Photos →",
    "fv.door.hover": "View Hover Report →",
    "fv.printAll": "Print all",
    "fv.sheetsEmpty": "Elevation sheets — no completed run with walls",
    "fv.perWall": "Per-wall takeoff",
    "fv.perWall.sub": "· AI-read, server-computed — SSOT",
    "fv.th.wall": "Wall",
    "fv.th.body": "Body ft²",
    "fv.th.gable": "Gable ft²",
    "fv.th.dormer": "Dormer ft²",
    "fv.th.total": "Total ft²",
    "fv.wall.front": "front",
    "fv.wall.back": "back",
    "fv.wall.left": "left",
    "fv.wall.right": "right",
    "fv.gables.title": "Contractor gables — photo-taped, scale-anchored",
    "fv.gables.sub": "· own waste factor assignable · not auto-injected into the estimate",
    "fv.dormers.title": "Contractor dormers — photo-taped, scale-anchored",
    "fv.noScale": "marked — no scale ref on photo",
    "fv.frontFace": "Front face",
    "fv.cheeks": "+ cheeks",
    "fv.defaultDepth": "(default depth)",
    "fv.total": "Total",
    "fv.depthFt": "depth (ft)",
    "fv.tapedDims.title": "Appendage dims — tape to override",
    "fv.tapedDims.sub": "· user-measured dims re-derive the math; assumed dims never enter it",
    "fv.appendage": "Appendage",
    "fv.wallRow": "{kind} — {wall} wall",
    "fv.depth": "Depth",
    "fv.height": "Height",
    "fv.printedOnPlans": "{v} ft — printed on plans",
    "fv.assumedDepth": "~{v} ft — assumed, not measured",
    "fv.assumedHeight": "above roofline — assumed, not measured",
    "lpp.runLine": "run {id} · {n} lines",
    "lpp.source": "source: {label}",
    "lpp.waste": "waste: {n}% — Waste % field",
    "lpp.geometry": "geometry: {label}",
    "lpp.defaultProfile": "Default profile:",
    "lpp.revert": "Revert",
    "lpp.flag.closed": "closed",
    "lpp.flag.reopen": "reopen",
    "lpp.flag.save": "Save",
    "lpp.flag.fieldVerify": "Field-verify",
    "lpp.flag.markVerified": "Mark verified",
    "lpp.flag.placeholder": "wall heights ft, e.g. 9, 9, 18.5, 9",
    "lpp.compare": "Compare Lap vs B&B",
    "lpp.hideCompare": "Hide compare",
    "lpp.derivedNote": "{n} derived line(s) — quantities & provenance live on the group tab lines below · colors on the Job Info MATERIAL COLORS block · pricing on the estimate tabs.",
    "lpp.pendingCount": "{n} line(s) without a sheet price — escalated by name",
    "lpp.matrixNote": "— matrix informs, never forbids; verify with dealer.",
    "lpp.dimCrossCheck": "Dimension cross-check",
    "lpp.cmp.title": "Compare profiles — same geometry, same engine",
    "lpp.cmp.line": "Line",
    "lpp.cmp.current": "Current — Lap",
    "lpp.cmp.differ": "{n} line(s) differ by quantity",
    "lpp.cmp.shared": "({n} shared line(s) identical in both)",
    "lpp.cmp.note": "Comparison view only — quantities & derivations, nothing is saved or applied. Pricing lives on the estimate tabs.",
    "mls.loading": "Loading material list…",
    "mls.unavailable": "Material list unavailable",
    "mls.expired": "This link has expired.",
    "mls.notFound": "Link not found or revoked.",
    "mls.loadError": "Could not load the material list. Check your connection and try again.",
    "mls.updatedTitle": "Updated list available.",
    "mls.updatedBody": "This page shows the exact version printed on {date}. The estimate has changed since — ask your contractor for the latest printout.",
    "mls.requestSent": "Request sent — your contractor has been notified.",
    "mls.requestBtn": "Request updated list",
    "mls.sending": "Sending…",
    "mls.requestFailed": "Could not send — try again shortly.",
    "mls.title": "MATERIAL LIST — LP SMARTSIDE",
    "mls.readOnly": "Read-only · frozen as printed {date}",
    "mls.th.item": "Item",
    "mls.th.color": "Color",
    "mls.th.qty": "Qty",
    "mls.th.unit": "Unit",
    "mls.substitutedFrom": "substituted from {name} — re-derived",
    "mls.unpricedNote": "Material list — quantities & derivations. Pricing is provided on the estimate itself.",
    "mls.geometryFrozen": "Geometry: {label} · single source",
    "mls.derivedFrom": "Derived from confirmed AI measurements — run {id} · single source",
    "mls.backToEstimate": "← Back to estimate",
    "ars.loading": "Loading accuracy report…",
    "ars.unavailable": "Accuracy report unavailable",
    "ars.title": "AI Measurement Accuracy Report",
    "ars.frozen": "Read-only · frozen {date}",
    "ars.newer": "Newer scored runs exist since this report was frozen. This page shows the exact report that was shared — ask the contractor for a fresh link to see the latest.",
    "ars.frozenBodyNote": "The frozen report below is shown exactly as it was sealed (original document in English).",

    // --- Public Accept page ---
    "accept.loading": "Loading your estimate…",
    "accept.notFound.title": "Estimate Not Found",
    "accept.notFound.expired": "This estimate link is invalid or has expired.",
    "accept.notFound.generic": "Could not load the estimate.",
    "accept.success.heading": "You're all set, {name}.",
    "accept.success.body": "We've notified {company} that you accepted estimate {number} for {amount}.",
    "accept.success.next": "They'll reach out within one business day to schedule next steps.",
    "accept.eyebrow": "Confirm Acceptance",
    "accept.heading": "Accept Estimate {number}",
    "accept.from": "From",
    "accept.totalPrice": "Total Price",
    "accept.preparedFor": "Prepared for:",
    "accept.model3d.title": "Your Home — Interactive 3D",
    "accept.model3d.note": "Built from AI photo measurements of your home — the same measurements this quote is priced from.",
    "accept.model3d.hint": "Drag to rotate · scroll to zoom.",
    "accept.model3d.attestation": "{count} location(s) field-confirmed · {initials} · {date}",
    "accept.model3d.simplifiedTitle": "Your Home — Simplified 3D Sketch",
    "accept.model3d.fitNote": "Simplified representation — not to scale/shape.",
    "email.model3dSimplifiedTitle": "Simplified 3D Sketch",
    "email.model3dFitNote": "Simplified representation — not to scale/shape.",
    "accept.share.title": "Share This Estimate",
    "accept.share.note": "Share with anyone who needs to see this — the link and QR code open this same page.",
    "accept.share.copy": "Copy link",
    "accept.share.copied": "Link copied",
    "accept.confirmHint": "Check the box above to enable confirmation.",
    "accept.suppliedBy": "Materials supplied by {supplier}",
    "accept.optionalNote": "Optional note for the contractor",
    "accept.notePlaceholder": "e.g. Can we start next week? Or, please bring extra trim samples.",
    "accept.consent": "I, {name}, accept this estimate for {amount} as quoted by {company}.",
    "accept.consent.fallback": "the customer",
    "accept.submit": "Confirm Acceptance →",
    "accept.submitting": "Sending acceptance…",
    "accept.disclaimer": "By accepting, you authorize {company} to begin scheduling. You can still reply to the original email with any changes.",
    "accept.secureLink": "Secure customer-acceptance link.",

    // --- Email/PDF body strings (used by emailQuote.js for the customer copy) ---
    "email.estimate": "Estimate",
    "email.validThrough": "Valid through {date}",
    "email.preheader": "Estimate {number} — {amount} total. Valid 30 days.",
    "email.preparedFor": "Prepared For",
    "email.estimator": "Estimator",
    "email.billing": "Billing",
    "email.scopeOfWork": "Scope of Work",
    "email.elevationTitle": "Per-Elevation Siding Breakdown",
    "email.elevationFront": "Front",
    "email.elevationBack": "Back",
    "email.elevationLeft": "Left",
    "email.elevationRight": "Right",
    "email.elevationTotal": "Total Siding Area",
    "email.jobPhotos": "Job Photos",
    "email.windowsOpenings": "Windows",
    "email.model3d": "Your Home — 3D Model",
    "email.model3dNote": "Built from AI photo measurements of your home — the same measurements this quote is priced from.",
    "email.model3dVerifyNote": "Some details are subject to on-site verification.",
    "email.total": "Total Price",
    "email.pricePending": "pricing to be confirmed",
    "email.pendingNote": "Items marked \"pricing to be confirmed\" are awaiting supplier price confirmation and are not included in the total. They will be quoted before work begins.",
    "email.validityWithDate": "This estimate is valid through {date}. Final price may vary based on site conditions discovered after work begins.",
    "email.validityGeneric": "Valid for 30 days from the date above. Final price may vary based on site conditions discovered after work begins.",
    "email.acceptCta": "Accept this Estimate  →",
    "email.replyHint": "Or just reply to this email with any questions.",
    "email.questionsFooter": "Questions about this estimate? Reply to this email and we'll get right back to you.",
    "email.materialsBy": "Materials supplied by {supplier}",
    "email.poweredBy": "Powered by Pro-Quote Estimating Tool",
    "email.materialsExcluded": "Materials excluded",
    "email.porchCeilingsIncluded": "Includes porch ceiling{plural}",
    "email.sentBy": "This estimate was sent by {company}.",
    "email.subjectWithCustomer": "Your siding estimate{num} from {company} — {customer}",
    "email.subjectGeneric": "Your siding estimate{num} from {company}",
    "email.greeting": "Hi {first},\n\nThanks for the opportunity to quote your project — the detailed estimate is below. Take a look and reply with any questions; happy to walk through anything that isn't clear.\n\n— {who}",
    "email.greetingFallbackName": "there",
    "email.greetingFallbackWho": "Your contractor",

    // Iter 38–40: window opening panels + snapshot cards + admin pricing
    "win.addOpening": "Add opening",
    "win.noOpenings": "No openings yet. Click <strong>Add opening</strong> to start quoting.",
    "win.width": "Width",
    "win.height": "Height",
    "win.qty": "Qty",
    "win.model": "Model",
    "win.ui": "UI",
    "win.base": "Base",
    "win.total": "Total",
    "win.outOfRange": "out of range",
    "win.options": "Window Options",
    "win.optionsPerWindow": "+{amt}/window",
    "win.perWindow": "Per window:",
    "win.openingsLabel": "{n} opening · type W × H below to price each window",
    "win.openingsLabelPlural": "{n} openings · type W × H below to price each window",
    "win.openingsLabelFixed": "{n} opening · pick model + sister color",
    "win.openingsLabelFixedPlural": "{n} openings · pick model + sister color",
    "win.sisterColor": "Sister Color (sets base)",
    "win.glassPackage": "Glass Package",
    "win.tempered": "Tempered Upcharge",
    "win.premiumOptions": "Premium Options",
    "win.premiumOptionsHint": "multi-select; price depends on UI bucket",
    "win.notesPlaceholder": "Kitchen — west wall (optional label / notes)",
    "win.addNote": "Add note",
    "win.removeOpening": "Remove opening",
    "win.hideNotes": "Hide notes",
    "win.optional": "optional",
    "win.none": "— None —",
    "win.upgradeOptions": "Upgrade Options",
    "win.snapshot": "Job Snapshot",
    "win.snapshot.subtitle": "openings · install + trim labor · material rolls",
    "win.snapshot.baseTotal": "Base Total",
    "win.snapshot.empty": "Add an opening above or enter a qty on any window-install / trim row below — totals will populate here in real time.",
    "win.snapshot.openings": "Window Openings",
    "win.snapshot.windowsCount": "{n} window",
    "win.snapshot.windowsCountPlural": "{n} windows",
    "win.snapshot.installTrim": "Install + Trim",
    "win.snapshot.installSub": "{n} line · labor",
    "win.snapshot.installSubPlural": "{n} lines · labor",
    "win.snapshot.material": "Material List",
    "win.snapshot.materialSub": "{n} roll line",
    "win.snapshot.materialSubPlural": "{n} roll lines",
    "win.mezzo.tempered.label": "Tempered Full",
    "win.mezzo.tempered.fullHint": "$9.18/sqft × {sqft}sqft",
    "win.colors.veroDesc": "vinyl base + laminate or painted finish",
    "win.colors.mezzoDesc": "extruded solid · FrameWorks · woodgrain",
    "win.color.exterior": "Exterior Color",
    "win.color.interior": "Interior Color",
    "win.color.select": "— Select —",
    "win.color.laminateWarn": "<strong>Heads up:</strong> Vero laminates are built on a <strong>white</strong> vinyl base only. Pair laminate with a white-base sister color, or switch the laminate side to a paint finish.",
    "win.color.laminateNotice": "Laminate selected — manufacturing will use a <strong>white</strong> vinyl base.",

    "win.bulkApply.title": "Apply to all windows?",
    "win.bulkApply.body": "Apply “{option}” to the other {count} uploaded windows too?",
    "win.bulkApply.hint": "Only windows whose product type supports this option will be updated. You can still tweak individual windows after.",
    "win.bulkApply.skip": "Just this one",
    "win.bulkApply.applyAll": "Apply to all {count}",

    // --- Tab picker modal (quote / material list) ---
    "tabPicker.title.quote": "Print Customer Quote",
    "tabPicker.title.material": "Print Material List",
    "tabPicker.helper.quote": "Pick which product lines to send to the customer. One PDF per pick keeps the quote uncluttered when the job spans multiple product lines.",
    "tabPicker.helper.material": "Pick which product lines to print for the supplier. Each pick gets its own list so the warehouse can pull and stage materials separately.",
    "tabPicker.cancel": "Cancel",
    "tabPicker.continue": "Continue · {n}",
    "tabPicker.label.vinyl": "Vinyl Siding",
    "tabPicker.label.ascend": "Ascend Composite Siding",
    "tabPicker.label.lp_smart": "LP SmartSide",
    "tabPicker.label.windows": "Windows",
    "tabLabel.vinyl": "Vinyl",
    "tabLabel.ascend": "Ascend",
    "tabLabel.lpSmart": "LP Smart",
    "tabLabel.windows": "Windows",
    "tabLabel.mezzo": "Mezzo",
    "nav.brandingAdmin": "Branding Admin",

    // --- Photo fill-in boxes (Three Doors step 6 + gate, ruled 2026-08-02) ---
    "pf.title": "Photo fill-ins — what the photos can't see",
    "pf.hint": "Photo door only. A box fills the hole the photos leave — it never overrides a measured value. An UNSET box BLOCKS the quote (scope not set — never $0). Type 0 if the house truly has none. Re-derives live.",
    "pf.soffit": "Soffit",
    "pf.dripEdge": "Drip edge",
    "pf.totalTrim": "Total trim",
    "pf.notSet": "Not set — blocks quote",
    "pf.friezeQ": "Frieze board?",
    "pf.yes": "Yes",
    "pf.no": "No",
    "pf.friezeHint": "Yes/No only — frieze LF derives from the measured runs (level = eaves {eaves} LF · sloped = rakes {rakes} LF). You never re-type a number the engine already has.",

    // --- Photo fill-in gate banner ---
    "gate.pf.title": "Photo fill-ins block this quote",
    "gate.pf.body": "PHOTO FILL-INS NOT SET — {count} box(es) open: {boxes}. SCOPE NOT SET, never $0 — the photos cannot see these; type the value (0 is a decision) or answer the frieze yes/no before this quote reaches a customer.",
    "gate.pf.hint": "Fill the boxes under Trade specs below — email, PDF and quote surfaces stay blocked until scope is set.",
    "gate.pf.box.soffit": "soffit ft²",
    "gate.pf.box.dripEdge": "drip edge LF",
    "gate.pf.box.totalTrim": "total trim ft²",
    "gate.pf.box.frieze": "frieze yes/no",

    // --- Derived color-tier chips (ruled 2026-08-02; tier terms verbatim) ---
    "tier.architectural": "Architectural tier — priced",
    "tier.standard": "Standard tier",
    "pf.history": "Fill-in history",
    "pf.lastSet": "Set by {by} · {date}",
    "pf.historyEmpty": "No fill-in changes recorded yet",
  },

  es: {
    "common.save": "Guardar",
    "common.saving": "Guardando…",
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.delete": "Eliminar",
    "common.loading": "Cargando…",
    "common.pleaseWait": "Un momento…",
    "common.welcomeBack": "Bienvenido de vuelta",
    "common.accountCreated": "Cuenta creada",
    "common.copied": "Copiado",

    "lang.toggle.aria": "Idioma",
    "lang.en": "EN",
    "lang.es": "ES",

    "theme.toggle.aria": "Tema",
    "theme.auto": "Auto (sistema)",
    "theme.orange": "Naranja de Seguridad",
    "theme.blueprint": "Azul Plano",
    "theme.forest": "Verde Bosque",
    "theme.steel": "Acero",
    "theme.highvis": "Amarillo Alta Visibilidad",
    "theme.dark": "Oscuro de Obra",
    "theme.status": "Tema: {name}",
    "theme.blurb": "No afecta las cotizaciones del cliente — la vista previa, PDFs y correos mantienen el estilo neutral del documento.",

    "nav.estimates": "Estimados",
    "nav.catalog": "Catálogo",
    "nav.team": "Equipo",
    "nav.brandingAdmin": "Admin de marca",
    "nav.logout": "Cerrar sesión",
    "nav.estimatorTag": "Estimador",
    "nav.role.owner": "Dueño",
    "nav.role.estimator": "Estimador",

    "auth.dashboard": "Panel",
    "auth.sidingEstimator": "Estimador de Vinil",
    "auth.welcomeBack": "Bienvenido de vuelta",
    "auth.createAccount": "Crear una cuenta",
    "auth.signIn": "Inicie sesión en su cuenta",
    "auth.freeTagline": "Estimador de vinil gratuito para nuestros distribuidores",
    "auth.yourName": "Su nombre",
    "auth.email": "Correo electrónico",
    "auth.password": "Contraseña",
    "auth.showPassword": "Mostrar contraseña",
    "auth.hidePassword": "Ocultar contraseña",
    "auth.lockedOut": "Bloqueado — no es una contraseña incorrecta",
    "auth.lockedOutHint": "Demasiados intentos fallidos desde esta conexión. La espera indicada es la del servidor. Un inicio de sesión correcto la borra de inmediato.",
    "auth.signInBtn": "Iniciar sesión",
    "auth.createBtn": "Crear cuenta",
    "auth.needAccount": "¿Necesita una cuenta?",
    "auth.register": "Registrarse",
    "auth.alreadyHaveOne": "¿Ya tiene una?",
    "auth.newCompany": "Empresa nueva",
    "auth.joinTeammate": "Unirme a equipo",
    "auth.yourCompanyName": "Nombre de su empresa",
    "auth.companyNamePlaceholder": "p. ej. Smith & Sons Siding",
    "auth.accessCodeFrom": "Código de acceso de {supplier}",
    "auth.accessCodePlaceholder": "ALSIDE-XXXXXX",
    "auth.teammateInviteCode": "Código de invitación del equipo",
    "auth.invitePlaceholder": "ABCD1234",
    "auth.inviteHelp": "¿Se une a una empresa existente? Pida el código de 8 caracteres a un compañero.",
    "auth.providedBy": "Proporcionado por {supplier}",

    // --- Tu cuenta (SEND-194) ---
    "account.yourPassword": "Tu contraseña",
    "account.blurb": "Cambia la contraseña de tu propio acceso. Necesitas la actual, y se cerrará la sesión en todos los demás dispositivos.",
    "account.current": "Contraseña actual",
    "account.new": "Contraseña nueva",
    "account.confirm": "Confirma la contraseña nueva",
    "account.mismatch": "Las dos contraseñas nuevas no coinciden.",
    "account.changeBtn": "Cambiar contraseña",
    "account.changed": "Contraseña cambiada",
    "account.sessionsNote": "Se cerrará la sesión en otros dispositivos.",
    "account.lockedOut": "Bloqueado — demasiados intentos",
    "account.seededNote": "Nota: esta es la cuenta creada desde el valor ADMIN_PASSWORD del servidor. Tu contraseña nueva ahora tiene prioridad y no se sobrescribirá al reiniciar.",

    "dash.title": "Estimados",
    "dash.eyebrow": "Panel",
    "dash.exportCsv": "Exportar CSV",
    "dash.newEstimate": "Nuevo Estimado",
    "dash.search": "Buscar por cliente, dirección o número de estimado",
    "dash.drafts": "Borradores",
    "dash.drafts.sub": "En curso",
    "dash.sent": "Enviados",
    "dash.sent.sub": "{amount} pendientes",
    "dash.accepted": "Aceptados",
    "dash.accepted.sub": "{amount} ganados",
    "dash.winRate": "Tasa de Cierre",
    "dash.winRate.sub": "{won} de {total} enviados",
    "dash.filter.all": "Todos",
    "dash.filter.draft": "Borrador",
    "dash.filter.sent": "Enviado",
    "dash.filter.accepted": "Aceptado",
    "dash.col.estNum": "Estimado #",
    "dash.col.customer": "Cliente",
    "dash.col.address": "Dirección",
    "dash.col.sellPrice": "Precio Venta",
    "dash.col.actions": "Acciones",
    "dash.untitled": "Sin nombre",
    "dash.empty.title": "Aún no hay estimados",
    "dash.empty.sub": "Cree su primer estimado para comenzar.",
    "dash.badge.accepted": "Aceptado",
    "dash.badge.sent": "Enviado",
    "dash.confirmDelete": "¿Eliminar este estimado?",
    "dash.deleted": "Estimado eliminado",
    "dash.deleteTitle": "¿Eliminar",
    "dash.thisEstimate": "este estimado",
    "dash.itsLinked": "Está vinculado.",
    "dash.trashNote": "Eliminar lo mueve a la papelera — recuperable por 30 días con Deshacer.",
    "dash.cancel": "Cancelar",
    "dash.deleteConfirm": "Eliminar de todos modos",
    "dash.duplicated": "Estimado duplicado — datos del cliente borrados",
    "dash.duplicate.aria": "Duplicar",
    "dash.duplicate.title": "Duplicar este estimado",
    "dash.delete.aria": "Eliminar",

    "cat.title": "Catálogo de Precios",
    "cat.eyebrow": "Ajustes",
    "cat.tier": "Nivel: {name}",
    "cat.intro": "Los precios de material los fija su proveedor (solo lectura). La mano de obra es suya — sus cambios solo afectan a su empresa.",
    "cat.clearOverrides": "Limpiar Cambios",
    "cat.save": "Guardar Catálogo",
    "cat.col.item": "Artículo",
    "cat.col.unit": "Unidad",
    "cat.col.material": "Material $",
    "cat.col.labor": "Mano de Obra $",
    "cat.saved": "Catálogo guardado",
    "cat.resetConfirm": "¿Borrar todos sus cambios y usar los valores del nivel?",
    "cat.resetDone": "Restaurado a los valores del nivel",

    "est.jobInfo": "Información del Trabajo",
    "est.customer": "Cliente",
    "est.address": "Dirección",
    "est.contactInfo": "Contacto y Origen",
    "est.jobBilling": "Dirección del Trabajo y Facturación",
    "est.email": "Correo",
    "est.phoneCell": "Celular",
    "est.phoneAlt": "Teléfono Secundario",
    "est.fax": "Fax",
    "est.contactMethod": "Contacto Preferido",
    "est.contactMethod.cell": "Celular",
    "est.contactMethod.landline": "Teléfono Fijo",
    "est.contactMethod.email": "Correo",
    "est.contactMethod.text": "Texto",
    "est.company": "Empresa",
    "est.contactTitle": "Cargo del Contacto",
    "est.street": "Calle",
    "est.city": "Ciudad",
    "est.state": "Estado",
    "est.zip": "Código Postal",
    "est.billingSame": "Dirección de facturación igual a la del trabajo",
    "est.billingAddress": "Dirección de Facturación",
    "est.leadSource": "Origen del Cliente",
    "est.leadSource.referral": "Referencia",
    "est.leadSource.repeat_customer": "Cliente Repetido",
    "est.leadSource.web": "Web",
    "est.leadSource.social": "Redes Sociales",
    "est.leadSource.yard_sign": "Cartel de Jardín",
    "est.leadSource.truck_wrap": "Rótulo de Camión",
    "est.leadSource.home_show": "Feria del Hogar",
    "est.leadSource.supplier": "Proveedor",
    "est.leadSource.door_knock": "Puerta a Puerta",
    "est.leadSource.other": "Otro",
    "est.leadSourceDetail": "Detalle",
    "est.contactHint": "Agregue correo para enviar cotizaciones",
    "est.exampleLead": "p. ej.",
    "est.warnEmail": "No parece una dirección de correo válida",
    "est.warnPhone": "No parece un número de teléfono válido — se esperan 10 dígitos, p. ej. (412) 555-0100",
    "est.warnZip": "El código postal debe ser de 5 dígitos, o ZIP+4",
    "quote.emailWillSave": "Este correo se guardará en el estimado.",
    "est.estimateNum": "Estimado #",
    "est.date": "Fecha",
    "est.estimator": "Estimador",
    "est.scope": "Alcance del Trabajo / Notas",
    "est.colors": "Colores de Material",
    "est.color.siding": "Color del Vinil",
    "est.color.lpSiding": "Color del LP Siding",
    "est.color.trim": "Color del Trim",
    "est.color.ascend": "Color Ascend",
    "est.color.shake": "Color de Shake (Pelican Bay)",
    "est.color.boardBatten": "Color de Board & Batten",
    "est.color.accessories": "Color de Accesorios",
    "est.color.outsideCorner": "Color de Esquinas Exteriores",
    "est.color.soffitFascia": "Color de Plafón / Fascia",
    "est.color.windowWrap": "Color de Envoltura de Ventana",
    "est.color.gutter": "Color de Canalón / Bajante",
    "home.pickerTag": "Espacio de Trabajo",
    "home.pickerTitle": "Elige qué estás cotizando",
    "home.pickerSubtitle": "Elige una familia de cotizaciones para entrar al espacio correcto. Cada uno mantiene su propia lista de estimaciones.",
    "home.workspaceTag": "Espacio",
    "home.issGroupTitle": "Cotizaciones ISS",
    "home.issGroupDesc": "Catálogo ISS de un solo nivel. Siding, ventanas y construcción nueva con precio combinado de material + mano de obra.",
    "home.issGroupCta": "Abrir Cotizaciones ISS →",
    "home.contractorGroupTitle": "Cotizaciones de Contratista",
    "home.contractorGroupDesc": "Precios de contratista de varios niveles. Ventanas Vero, Vinilo + Ascend, y LP SmartSide — cada uno con su propio catálogo y reglas de mano de obra.",
    "home.contractorGroupCta": "Abrir Cotizaciones de Contratista →",
    "home.back": "← Volver",
    "home.iss.title": "Cotizaciones ISS",
    "home.iss.subtitle": "Elige qué tipo de cotización ISS estás creando.",
    "home.contractor.title": "Cotizaciones de Contratista",
    "home.contractor.subtitle": "Elige qué catálogo de contratista usará esta cotización.",
    "home.issTitle": "Cotizaciones ISS Remolding Siding ASCEND/Vinyl",
    "home.issDesc": "Estimador de revestimiento de un solo nivel usando el catálogo ISS 2026. Un precio combinado de material + mano de obra por línea, 9 secciones estándar.",
    "home.issCta": "Abrir ISS Siding →",
    "home.issWindowsTitle": "Cotizaciones de Ventanas de Reemplazo ISS",
    "home.issWindowsDesc": "Ventanas estilo Vero en el catálogo ISS. Refleja el flujo actual de ventanas con precio combinado de material + mano de obra.",
    "home.issWindowsCta": "Abrir Ventanas ISS →",
    "home.issNewConTitle": "Cotizaciones ISS Siding Construcción Nueva",
    "home.issNewConDesc": "Artículos de revestimiento para construcción nueva. Hoja de precios aún no cargada — este espacio se abrirá cuando se proporcione el catálogo.",
    "home.issNewConCta": "Próximamente",
    "home.sidingTitle": "Cotizaciones de Vinilo + Ascend",
    "home.sidingDesc": "Vinil, Ascend Composite, soffit, canalones, accesorios. Estimador de varias pestañas con auto-relleno de HOVER, paletas de colores y listas de materiales.",
    "home.sidingCta": "Abrir Vinilo + Ascend →",
    "home.windowsTitle": "Cotizaciones de Ventanas",
    "home.windowsDesc": "Ventanas Vero + puertas corredizas de vidrio con instalación, opciones de mejora, prácticas libres de plomo y selección de color por ventana.",
    "home.windowsCta": "Abrir Cotizaciones de Ventanas →",
    "home.underConstruction": "En Construcción",
    "home.lpTitle": "Cotizaciones LP SmartSide",
    "home.lpDesc": "LP SmartSide — tabla de hebras, paneles, molduras y accesorios. Espacio dedicado con precios por nivel de margen y mano de obra en $0 por defecto.",
    "home.lpCta": "Abrir LP SmartSide →",
    "home.cardSidingTitle": "Cotizaciones de Revestimiento",
    "home.cardWindowsTitle": "Cotizaciones de Ventanas ISS",
    "est.colors.windows": "Colores de Ventanas",
    "est.color.windowFrame": "Color del Marco",
    "est.color.windowInterior": "Color Interior",
    "est.color.windowExterior": "Color Exterior",
    "est.color.placeholder": "p. ej. Gris Tormenta",
    "est.sync.headline": "Los precios del catálogo se actualizaron",
    "est.sync.subline": "{n} artículo(s) en este borrador han cambiado de precio desde la última vez que lo guardó",
    "est.sync.dismiss": "Descartar",
    "est.sync.review": "Revisar y Sincronizar",
    "est.sync.modal.title": "Sincronizar con el Catálogo Más Reciente",
    "est.sync.modal.subtitle": "Revise {n} cambio(s) abajo — los totales se actualizarán al aplicar",
    "est.sync.col.item": "Artículo",
    "est.sync.col.qty": "Cant.",
    "est.sync.col.matOld": "Mat (ant.)",
    "est.sync.col.matNew": "Mat (nuevo)",
    "est.sync.col.labOld": "Mano O. (ant.)",
    "est.sync.col.labNew": "Mano O. (nuevo)",
    "est.sync.cancel": "Cancelar",
    "est.sync.applyButton": "Aplicar {n} y Guardar",
    "est.sync.applying": "Aplicando…",
    "est.sync.toast": "{n} artículo(s) sincronizados con los precios actuales del catálogo",
    "est.wasteFactor": "Factor de Desperdicio",
    "est.wasteSuffix": "% material extra",
    "est.wasteHint": "Se aplica a Vinil + Ascend Composite Lap/B&B + LP SmartSide Lap (38 Series — sellado 2026-07-19: la fórmula no incluye merma; este campo es la única merma aplicada)",
    "est.overhang": "Alero del techo",
    "est.overhangHint": "Determina la cantidad de piezas de soffit (12\" por defecto)",
    "est.salesTax": "Impuesto",
    "est.applyTaxOnMaterial": "Aplicar impuesto al material",
    "est.profit": "Ganancia",
    "est.margin": "Margen s/venta",
    "est.markup": "Recargo s/costo",
    "est.marginSuffix": "% de su precio de venta como ganancia — su número (venta = costo ÷ (1 − %))",
    "est.markupSuffix": "% agregado sobre su costo — su número (venta = costo × (1 + %))",
    "est.jobPhotos": "Fotos del Trabajo",
    "est.addPhoto": "Agregar",
    "est.noPhotos": "Aún no hay fotos.",
    "est.photoAdded": "Foto agregada",
    "est.summary": "Resumen",
    "est.sum.material": "Material",
    "est.sum.waste": "Desperdicio de corte dentro de Material ({pct}% del Material)",
    "est.sum.tax": "Impuesto ({pct}%)",
    "est.sum.labor": "Mano de Obra",
    "est.sum.baseCost": "Costo Base",
    "est.sum.sell": "Su precio al cliente ({pct}% {mode})",
    "lp.mat.title": "Lista de materiales (leída por IA)",
    "lp.fv.title": "Verificación en campo — ubicaciones leídas por IA en espera de confirmación",
    "lp.fv.note": "Las ubicaciones ámbar ya están incluidas en el conteo de piezas (garantía de presencia). Confirme cada una en sitio — verificar nunca cambia cantidades; reubicar mueve la característica detectada a su pared real; \u201cNo existe\u201d la elimina de los conteos (reversible).",
    "lp.fv.verify": "Confirmado en campo",
    "lp.fv.wrongWall": "Pared incorrecta",
    "lp.fv.notPresent": "No existe",
    "lp.fv.pickWall": "Pared correcta\u2026",
    "lp.fv.relocated": "Reubicada",
    "lp.fv.removed": "Eliminada",
    "lp.fv.wall": "pared",
    "lp.fv.verified": "Confirmado — en campo",
    "lp.fv.tape.title": "Mídalo en sitio — entrada rápida opcional",
    "lp.fv.tape.note": "Esta característica aún tiene dimensiones asumidas (solo para el dibujo, con nota en el PDF del cliente). Ingrese lo que mida — los valores ingresados se vuelven medidos por el usuario (reversible, recalcula cantidades). Omitir mantiene el estado asumido.",
    "lp.fv.tape.height": "Altura",
    "lp.fv.tape.depth": "Profundidad",
    "lp.fv.tape.save": "Guardar medidas",
    "lp.fv.tape.ft": "pies",
    "lp.or.title": "Confirmar aberturas — revisión previa a la derivación",
    "lp.or.note": "Ratificación de un toque de las aberturas detectadas por IA. Confirmar promueve la abertura a estado verificado; corregir el tipo re-deriva los conteos; marcar \u201cNo existe\u201d la elimina de todos los conteos y del cálculo de molduras (reversible). Omitible — las marcas sin confirmar persisten.",
    "lp.or.reviewed": "revisadas",
    "lp.or.unconfirmed": "sin confirmar",
    "lp.or.confirm": "Confirmar",
    "lp.or.confirmed": "Confirmada",
    "lp.or.corrected": "Corregida",
    "lp.or.correctedShort": "Corregida",
    "lp.or.wrongType": "Tipo incorrecto",
    "lp.or.pickType": "Tipo real…",
    "lp.or.viewPhoto": "Ver foto de origen",
    "lp.or.notPresent": "No existe",
    "lp.or.removed": "Eliminada",
    "lp.or.removedShort": "eliminadas",
    "lp.or.removeWarn": "Esta abertura alimenta líneas derivadas —",
    "lp.or.removeAnyway": "Eliminar de todos modos",
    "lp.or.cancel": "Cancelar",
    "lp.mat.colors": "Colores",
    "lp.mat.allComponents": "Todos los componentes",
    "lp.mat.edit": "Editar lista",
    "lp.mat.lock": "Terminar edición",
    "lp.mat.readonly": "Solo lectura — haga clic en Editar lista para sustituir productos. Las sustituciones son por sesión, por diseño: la lista siempre se recalcula desde la geometría más reciente.",
    "lp.mat.pending": "precio pendiente",
    "lp.mat.total": "Total de materiales",
    "lp.mat.noRun": "Ejecute primero Medición IA — la lista de materiales se deriva de medidas confirmadas.",
    "lp.mat.substituted": "sustituido de",
    "lp.mat.provenance": "Cómo se derivó este número",
    "est.customerQuote": "Cotización al Cliente",
    "est.materialList": "Lista de materiales",
    "est.tabOption": "opción {tab}",
    "est.print": "Imprimir",
    "est.exportCsv": "Exportar CSV",
    "est.barLabel": "Estimado",
    "est.untitled": "Sin nombre",
    "est.bar.base": "Base",
    "est.bar.derivedUnapplied": "Derivada — aún no aplicada",
    "est.bar.sell": "Venta",
    "est.bar.profit": "Ganancia",
    "est.loading": "Cargando estimado…",
    "est.col.item": "Artículo",
    "est.col.unit": "Unidad",
    "est.note.add": "+ nota",
    "est.note.chip": "nota",
    "est.note.prints": "Se imprime en la lista de materiales",
    "est.note.placeholder": "Nota del contratista para esta línea — se imprime en la lista de materiales",
    "est.col.mat": "Mat $",
    "est.col.qty": "Cant",
    "est.col.lab": "M.O. $",
    "est.col.total": "Total",
    "est.itemsBadge": "{n} artículos",
    "est.customDesc": "Descripción Personalizada",
    "est.customDescPlaceholder": "Descripción del artículo",
    "est.addCustomLine": "Agregar línea personalizada",
    "est.removeMisc": "Eliminar línea",

    "quote.subject": "Asunto",
    "quote.personalNote": "Nota personal (aparece arriba del estimado)",
    "quote.personalNoteHelp": "El cliente verá esta nota primero, y luego el estimado.",
    "quote.recipientPlaceholder": "cliente@ejemplo.com",
    "quote.emailBtn": "Enviar",
    "quote.sending": "Enviando…",
    "quote.downloadPdf": "Descargar PDF",
    "quote.emailNotConfigured": "Servicio de correo no configurado. Agregue RESEND_API_KEY en backend/.env para habilitarlo.",
    "quote.langPicker": "Enviar en:",
    "quote.sentToast": "Correo enviado",
    "quote.docSubtitle": "Estimado · Cotización",
    "quote.signature": "Firma del Cliente",
    "quote.dateSigned": "Fecha",
    "quote.printBlocked": "IMPRESIÓN BLOQUEADA: {n} elemento(s) — resuelva la lista de preparación antes de que esta cotización llegue al cliente.",
    "quote.printBlockedMore": "… y {n} más (vea Preparación en la página del estimado)",
    "quote.printBlockedTooltip": "IMPRESIÓN BLOQUEADA — resuelva primero la lista de preparación",
    "gates.finalJob": "Superficie de trabajo final",
    "gates.badge.quote": "¿Listo para cotizar?",
    "gates.badge.order": "¿Listo para ordenar?",
    "gates.badge.clear": "LIBRE",
    "gates.badge.blocked": "BLOQUEADO",
    "gates.quoteBlockers": "Aún no listo para cotizar — esto bloquea correo · página de aceptación · PDF · congelado · QR:",
    "gates.orderItems": "¿Listo para ordenar? — los campos medidos con cinta se cierran aquí y reemplazan los valores derivados:",
    "gates.releaseMaterials": "Liberar materiales",
    "gates.releasing": "Liberando…",
    "gates.orderBlocked": "Aún no listo para ordenar",
    "gates.releasedBy": "Materiales liberados por {by} @ {at}",
    "readiness.title": "Preparación del estimado",
    "readiness.refresh": "Actualizar",
    "readiness.checking": "Verificando…",
    "readiness.unavailable": "Preparación no disponible.",
    "readiness.allClear": "Todo listo — nada se interpone entre usted y un número real.",
    "readiness.openCount": "{n} elemento(s) abierto(s)",
    "readiness.footer": "Los elementos marcados como bloqueantes detienen la cotización al cliente — el resto son avisos.",
    "readiness.kind.pending_price": "Precios pendientes",
    "readiness.kind.open_flag": "Indicadores abiertos",
    "readiness.kind.field_verify": "Verificación en campo",
    "readiness.kind.unpriced_row": "Filas sin precio",
    "readiness.kind.labor_pending_row": "Mano de obra pendiente — el contratista fija el precio",
    "readiness.kind.labor_pending": "Mano de obra sin decidir",
    "readiness.kind.qty_pending": "Cantidad pendiente",
    "readiness.kind.family_conflict": "Conflicto de familia",
    "readiness.kind.quote_gate": "Preparación para cotizar",
    "est.readinessBtn": "Preparación",
    "est.derivedChip": "derivado",
    "jip.pending.title": "Pendiente — aún no aplicado ({n})",
    "jip.pending.blueprint": "Plano",
    "jip.pending.photo": "Foto",
    "jip.pending.row": "Corrida de {door} {date}{sqft} — nunca se cotiza hasta que la revise y aplique",
    "jip.pending.sqft": " — {n} ft² de revestimiento",
    "jip.pending.more": "…y {n} más",
    "jip.collapse": "Contraer",
    "jip.edit": "Editar",
    "jip.expand": "Expandir",
    "jip.tile.blueprints": "Planos",
    "jip.tile.aiPhoto": "Dibujar sobre fotos",
    "oneDoor.listTitle": "Fotos de este trabajo — usted elige quién lee cada una",
    "oneDoor.capture": "Tomar fotos",
    "oneDoor.aiMayRead": "La IA puede leerla",
    "oneDoor.handOnly": "Solo a mano",
    "oneDoor.clear": "Borrar",
    "oneDoor.noChoice": "Sin decisión",
    "oneDoor.deleteNo": "No",
    "oneDoor.deleteYes": "Sí, borrar",
    "oneDoor.faceNone": "sin cara etiquetada",
    "oneDoor.stateAi": "La IA puede leer esta foto",
    "oneDoor.stateHand": "Solo a mano — sin lectura de IA",
    "oneDoor.stateNone": "Sin decisión registrada",
    "intake.importHover": "Importar HOVER",
    "intake.reading": "Leyendo…",
    "intake.readBlueprints": "Dibujar sobre planos",
    "intake.aiMeasure": "Dibujar sobre fotos",
    "intake.aiMeasureResume": "Dibujar sobre fotos (Reanudar)",
    "hov.cutNote": "Estimado LP — las líneas de composición se derivan por el motor LP al Aplicar (EL CORTE). Esta puerta no mezcla líneas de vinil/Ascend/ventanas.",
    "hov.applyLp": "Aplicar y derivar lista LP",
    "hov.restore": "Restaurar líneas HOVER",
    "bp.restore": "Restaurar lectura del plano",
    "bp.restoreTitle": "Reabrir la lectura de planos más reciente — {pg} páginas, hace {min} min. Solo visualización, sin nueva lectura.",
    "err.pdf.generic": "No se pudo crear el PDF — revise su conexión e intente de nuevo.",
    "err.gate.quote": "Aún no se puede enviar ni imprimir la cotización — algunas cosas todavía necesitan un precio o una decisión:",
    "err.gate.materials": "Aún no se puede imprimir la lista de materiales — faltan resolver algunas lecturas:",
    "err.gate.order": "Aún no se pueden liberar estos materiales — termine primero las verificaciones de campo:",
    "err.gate.action": "Resuelva esto e intente de nuevo",
    "err.gate.more": "+{n} más",
    "err.spec.lines": "Su ajuste se guardó, pero las líneas de material no se actualizaron — presione RE-DERIVAR para ponerlas al día.",
    "hov.print": "Imprimir",
    // ---- Tarjeta de lectura de verificación del plano (solo lectura) ----
    "bp.rb.title": "Lectura de verificación — lo que leyó la app",
    "bp.rb.readonly": "Solo visualización — lee la corrida, no cambia nada",
    "bp.rb.run": "corrida",
    "bp.rb.reap": "se purga el {when} salvo que se aplique/archive",
    "bp.rb.archived": "archivada — nunca se purga",
    "bp.rb.sheets": "Elevaciones sintéticas (Fase 1)",
    "bp.rb.sheets.note": "generadas desde el modelo del plano — posiciones esquemáticas, nunca la hoja arquitectónica",
    "bp.rb.planes": "Censo de planos de techo",
    "bp.rb.plane": "Plano",
    "bp.rb.eave": "Alero LF",
    "bp.rb.rake": "Inclinado LF",
    "bp.rb.gableEnds": "Hastiales",
    "bp.rb.porchSqft": "Porche ft²",
    "bp.rb.totals": "Totales de planos",
    "bp.rb.gableBlind": "INCLINADO 0 en \"{plane}\" — plano ciego al hastial. Si el alzado muestra esta ala con su propio hastial, la lectura perdió sus inclinados. Verifique.",
    "bp.rb.noPlanes": "SIN PLANOS DE TECHO LEÍDOS — solo rectángulo de cuatro paredes. Cada alero e inclinado de ala (garaje, porche, hastial cruzado) es invisible para esta lectura.",
    "bp.rb.noGarage": "SIN PLANO DE GARAJE — los planos muestran un garaje pero no se leyó su techo (o vino ciego al hastial). Faltan los tramos de alero/inclinado del garaje.",
    "bp.rb.corners": "Libro de esquinas",
    "bp.rb.outside": "Exteriores",
    "bp.rb.inside": "Interiores",
    "bp.rb.invariant": "Invariante del recorrido (ext − int = 4)",
    "bp.rb.pass": "CUMPLE",
    "bp.rb.fail": "FALLA — repita el recorrido del contorno",
    "bp.rb.basis.per_corner": "Base del LF de esquinas: alturas por esquina, sumadas",
    "bp.rb.basis.averaged": "Base del LF de esquinas: cantidad × altura PROMEDIO — verifique. Promediar mezcla esquinas altas de dos pisos con esquinas bajas de ala.",
    "bp.rb.basis.missing": "Sin LF de esquinas leído",
    "bp.rb.wing": "La huella {fp} ft² excede el rectángulo de paredes {rect} ft² — existe un ala saliente. Un recorrido solo-rectángulo perdió sus esquinas.",
    "bp.rb.porch": "Porche",
    "bp.rb.porch.plane_read": "Plano de porche leído — cielo {sqft} ft²",
    "bp.rb.porch.plane_without_ceiling": "Plano de porche SIN cifra de cielo — verifique la planta",
    "bp.rb.porch.phantom_ceiling": "¿FANTASMA? Cielo de porche de {sqft} ft² SIN plano de porche — una etiqueta sobre nada. Verifique contra la planta.",
    "bp.rb.porch.absent": "Sin porche leído — verifique la planta (los porches cubiertos se pierden con frecuencia)",
    "bp.rb.cornerHeights": "Alturas por esquina",
    "bp.rb.heights.read": "Alturas por esquina leídas — cada esquina toma su pared más alta, hasta el alero. Estas gobiernan la línea de esquinas.",
    "bp.rb.heights.undim": "{n} esquina(s) sin dimensión impresa — se mantienen en 1 pieza, la bandera queda.",
    "bp.rb.gutterRuns": "Inventario de tramos de canaleta",
    "bp.rb.gutterRuns.governs": "Estos tramos gobiernan la línea de canaleta — la suma de aleros por plano sigue siendo la cifra del sofito.",
    "bp.rb.gutterRuns.none": "Sin inventario de tramos en esta lectura — la canaleta usa la suma de aleros por plano. Una relectura trae los tramos.",
    "bp.rb.source": "Archivo original",
    "bp.rb.source.native": "Archivo original guardado. Texto impreso en {n} de {m} páginas — las dimensiones existen como texto real en este archivo, no solo píxeles.",
    "bp.rb.source.mixed": "Archivo original guardado. Texto impreso en {n} de {m} páginas — el resto son solo imagen, sus números son lecturas de visión.",
    "bp.rb.source.scan": "Archivo original guardado. Este archivo no tiene capa de texto — las páginas son imágenes, así que cada número de esta tarjeta es una lectura de visión.",
    "bp.rb.source.images": "Hojas de foto guardadas tal como se subieron. Cada número de esta tarjeta es una lectura de visión.",
    "bp.rb.consistency": "Control de consistencia",
    "bp.rb.consistency.clean": "Sin contradicciones entre los números de esta lectura.",
    "bp.rb.consistency.corner_taller_than_wall": "{n} esquina(s) leyeron {h}' (libro de esquinas) mientras la tabla de paredes lee {wall}' — dos fuentes leídas por IA discrepan sobre la misma casa. Esta tarjeta no resuelve hacia NINGUNA: ambas son lecturas de visión. Una altura medida con cinta o ingresada por el contratista supera a ambas.",
    "bp.rb.consistency.corner_lf_not_sum": "El LF de esquinas imprime {lf} pero sus propias alturas por esquina suman {sum}. Un total declarado debe igualar la suma de sus partes.",
    "bp.rb.consistency.corner_walk_conflict": "Dos lecturas de IA discrepan sobre el recorrido de esquinas — la lectura principal {p_out} exteriores / {p_in} interiores, la pasada de techo {r_out} exteriores / {r_in} interiores. LA PRINCIPAL SE MANTIENE; ninguna se adopta como verdad. Marque las elevaciones y cuente — un conteo medido o del contratista supera a ambas.",
    "bp.rb.consistency.eave_rake_orientation": "La cifra de aleros ({eaves} LF) coincide con el par de muros de GABLETE ({gsum} LF) más que con el par de aleros ({esum} LF). En un techo a dos aguas, aleros y bordes inclinados van en pares de muros opuestos — esta lectura puede tener la casa rotada 90°. Verifique qué muros llevan las canaletas antes de ordenar.",
    "bp.rb.consistency.opposing_walls_disagree": "Los muros opuestos discrepan — {pair}: {a}' vs {b}'. Un cuerpo rectangular tiene muros opuestos iguales; una diferencia aquí suele significar un ala proyectada o una lectura errónea. La verificación de caja ahora usa el MÁS CORTO del par para que un ala oculta aún pueda detectarse. Marque qué muro lleva el quiebre.",
    "bp.rb.consistency.gable_census_mismatch": "El censo de planos cuenta {planes} hastiales pero solo {walls} pared(es) llevan triángulo de hastial — un hastial sin pared donde vivir.",
    "bp.rb.consistency.box_model": "Frente=atrás e izquierda=derecha EXACTAMENTE, pero la huella prueba un ala saliente. Las paredes del ala faltan en la lectura — parece un modelo de caja, no una lectura.",
    "bp.rb.consistency.run_exceeds_facade": "El tramo de canaleta {label} imprime {run}' pero la pared {label} mide solo {wall}' — un tramo continuo no puede exceder su fachada.",
    "bp.rb.consistency.wall_segments_mismatch": "Las secciones de altura de la pared {label} suman {sum}' de ancho contra una pared de {width}' — el recorrido de secciones no cierra. El cálculo de siding mantuvo el rectángulo en vez de consumir un recorrido roto.",
    "bp.rb.consistency.footprint_missing": "No se capturó huella impresa en esta lectura — el control de ala (huella vs rectángulo de paredes) NO PUEDE ejecutarse. Su ausencia se nombra, no se calla.",
    "bp.rb.consistency.porch_run_vs_width": "El tramo de canaleta del porche imprime {run}' mientras el ancho impreso del porche es {width}' — un porche, dos respuestas. Ambas son lecturas; ninguna gobierna hasta verificarse.",
    "bp.rb.consistency.porch_dims_vs_area": "El porche imprime {w}' × {d}' = {product} ft², pero la tabla de áreas dice {area} ft² — las dimensiones declaradas no reproducen el área declarada. Falta alguna pulgada en una dimensión.",
    "bp.rb.consistency.window_size_parse_mismatch": "Ventana {mark}: la cadena del cuadro \"{printed}\" se convierte a {parsed} in, pero la lectura lleva {carried} in — la transcripción se contradice.",
    "bp.rb.consistency.mark_size_conflict": "La marca {mark} lleva dos tamaños impresos distintos: {sizes}. Las marcas pueden haberse mezclado entre hojas del cuadro — revise la fila de esta marca en cada hoja.",
    "bp.rb.consistency.count_column_mismatch": "Marca {mark}: las celdas de CANTIDAD del cuadro dicen {cells} (suma {summed}), pero la lectura lleva {carried}. La columna de cantidad gobierna — una cantidad que la ignora es contar símbolos.",
    "bp.rb.consistency.schedule_count_unread": "Marca {mark} ({kind}): la fila del cuadro RECHAZA su cantidad — {reason}. Nada se estima; la marca no aporta cantidad hasta que la celda se lea o un humano la proporcione.",
    "bp.rb.consistency.schedule_row_recovered": "Puerta {mark} recuperada de la fila impresa del cuadro en la hoja {page} ({type}) — la lectura del modelo la omitió. Solo se lleva lo que la fila imprime.",
    "bp.rb.consistency.openings_unplaced": "{count} abertura(s) derivadas del cuadro van al grupo SIN UBICAR — un cuadro da tamaño y cantidad y calla la ubicación por diseño. Ninguna fachada se asigna hasta que exista una lectura de ubicación. La deducción de aberturas se aplica al AGREGADO — no se atribuye por fachada.",
    "bp.rb.consistency.dims_unattributed_quantity_refused": "{count} dimensión(es) localizada(s) pero SIN ATRIBUCIÓN — {dims}. Una misma cifra impresa la reclaman varias fachadas, así que la pertenencia no está establecida: la cifra se muestra aquí y se RECHAZA en toda cantidad (área de muro y hastial, arranque, perímetro, aleros, faldones). Establezca a qué fachada pertenece antes de valorar.",
    "bp.rb.consistency.faces_refused": "{count} fachada(s) RECHAZADAS — {faces}. Su altura o ancho no pudo establecerse desde sus propios alzados; cada una no aporta NADA hasta medirse con cinta o hasta una lectura de ubicación. El silencio aquí significaría que nada está mal — y no es así.",
    "bp.rb.consistency.opening_sizes_refused": "{count} marca(s) de abertura rechazaron su tamaño — {marks}. Cada una se cuenta pero aporta 0 ft² a cualquier deducción hasta que su tamaño impreso se lea o una persona lo indique.",
    "bp.rb.consistency.deduction_refused": "La deducción de aberturas fue RECHAZADA — {rows} fila(s) de aberturas leídas, pero {count} fachada(s) rechazadas ({faces}). Una abertura solo puede deducirse de un bruto que incluya la fachada donde está, y no existe lectura de ubicación. No se dedujo nada.",
    "bp.rb.consistency.page_rotation_normalized": "{count} página(s) estaban rotadas en el ráster y se normalizaron antes de la lectura — páginas {pages}. La detección es el colapso de la proporción vertical entre los tres pases de OCR; el corte viene de la brecha observada, no de un número redondo.",
    "bp.rb.consistency.lf_lane_refused": "{count} línea(s) de LF calculadas RECHAZADAS — {lanes}. Llegan como aritmética del modelo sobre las dimensiones de muro, y las dimensiones en que se apoyan fueron anuladas por la guardia de evidencia — un total calculado no puede sobrevivir a sus insumos. El motivo se indica entre paréntesis junto a cada línea. Mida con cinta o ingrese los tramos a mano.",
    "bp.rb.consistency.below_grade_unread": "Hay un nivel BAJO RASANTE dibujado ({pages}) — esta lectura no tiene ruta para bajo rasante / walkout. Ningún revestimiento del walkout está incluido y nada de lo mostrado lo cuenta. Marque el walkout a mano hasta que exista una regla de walkout.",
    "bp.rb.consistency.material_claims_unconfirmed": "{count} indicación(es) de material esperando confirmación humana — {claims}. Las indicaciones de material son lectura del modelo, nunca verificadas contra el plano; mueven pies cuadrados entre perfiles de revestimiento (y un % bloqueado se revirtió a 100). Confirme o corrija cada una antes de valorar el despiece.",
    "bp.rb.consistency.page_rotation_indeterminate": "{count} página(s) cayeron en la brecha de rotación — páginas {pages}. NUNCA se normaliza por conjetura: cada una queda como se rasterizó y se lee de ambas formas; un desacuerdo entre ambas lecturas se imprimiría aquí.",
    "sp.defaulted": "PERFIL POR DEFECTO — se asumió {name}, no fue una elección ({qty} SQ dependen de él). Toque para elegir el perfil; la cantidad se mueve con su elección.",
    "sp.chosen": "Perfil — {name} (su elección). Toque para cambiar; la cantidad se mueve con él.",
    "sp.stale": "Su elección de perfil ({name}) ya no lleva la cantidad — una reaplicación la devolvió a {carrier}. Toque para volver a elegir.",
    "sp.choose": "Elegir perfil de siding",
    "sp.pickTitle": "Elija el perfil de siding — la cantidad actual se mueve a su elección",
    "sp.none": "No hay perfiles de siding de vinilo en el catálogo para este presupuesto.",
    "bp.rb.consistency.door_size_parse_mismatch": "Puerta {mark}: el cuadro imprime \"{printed}\" ({parsed} in), pero la lectura lleva {carried} in — el tamaño impreso gobierna; una persona debe revisar esta fila.",
    "bp.rb.rail.ocr_quote_miss": "Una lectura de texto por máquina de la hoja no encuentra el impreso que la lectura cita: {text}. Dos lecturas independientes de los mismos píxeles discrepan — una está mal. Revise la hoja; ninguna gana por defecto.",
    "bp.va.ocrloc": "UBICADO POR LECTURA DE TEXTO POR MÁQUINA — la caja es donde una lectura local de texto (OCR) encuentra el impreso citado en la imagen de la hoja. El VALOR sigue viniendo de la lectura, nunca del localizador.",
    "bp.rb.consistency.wall_segment_undimensioned": "La sección \"{section}\" de la pared {label} NO tiene altura impresa — el plano no tiene número para ella. Señalado, nunca adivinado; el siding de la pared mantuvo el rectángulo hasta que llegue una dimensión real.",
    "bp.rb.consistency.footprint_is_total_finished": "La huella lee {fp} ft² — igual a la cifra TOTAL TERMINADO ({tf} ft²) con un segundo piso presente. Pisos sumados es área habitable, no una huella. Las cantidades etiquetadas se leen como se etiquetan.",
    "bp.rb.rail": "Indicadores de honestidad",
    "bp.rb.rail.pitch": "Pendiente (impresa): {text}",
    "bp.rb.rail.no_pitch": "SIN PENDIENTE impresa — pendiente supuesta. Verifique contra los alzados.",
    "bp.rb.rail.scale_confidence": "Confianza de escala: {text}",
    "bp.rb.rail.roof_pass_merge": "Fusión de pasada de techo aceptada: {text}",
    "bp.rb.rail.roof_pass_rejected": "Sobrescritura de la pasada de techo RECHAZADA — {text}. Un valor con evidencia nunca se reemplaza por uno sin evidencia; la cifra citada de la lectura principal se mantiene.",
    "bp.rb.rail.pages_truncated": "ESTE JUEGO DE PLANOS TIENE MÁS PÁGINAS DE LAS QUE LA APP LEYÓ — {text} (páginas del juego → páginas leídas). Las páginas descartadas son invisibles para la lectura, el localizador y cada censo. Divida el juego o suba el límite de páginas antes de confiar en este despiece.",
    "bp.rb.rail.read_notes": "Notas de lectura: {text}",
    "bp.rb.rail.overhang_printed": "Alero leído de los planos: {text}\".",
    "bp.rb.rail.overhang_default": "Alero SIN acotar en los planos — la app mantiene su valor por defecto. Verifique en campo antes de pedir soffit.",
    "bp.rb.rail.overhang_by_plane": "Alero impreso por plano — {text}.",
    "bp.rb.rail.overhang_missing_on_planes": "Alero SIN acotar en el/los plano(s) {text} — la lectura no resolvió una profundidad de alero aquí. Indique dónde midió, o verifique en campo antes de pedir.",
    "bp.rb.rail.pitch_missing_on_planes": "Pendiente SIN imprimir en el/los plano(s) {text} — la app NO hereda la pendiente del cuerpo principal aquí. La altura del gable queda nula hasta que se lea o se mida.",
    "bp.rb.rail.pitch_varies_by_plane": "La pendiente varía por plano — {text}. Un ala con pendiente más pronunciada calcula su propia altura; la hoja no promedia.",
    "bp.rb.rail.wall_height_by_plane": "Altura de pared impresa por plano — {text}. Se usa textualmente; los agregados hasta la altura revestida (placas + cimiento) se nombran por separado, nunca se suman en silencio.",
    "bp.rb.rail.dims_unverified": "Dims MARCADAS SIN VERIFICAR — la cita puede ser real pero no pudimos confirmarla cerca de su característica: {text}. Se conserva para revisión; no se alimenta al costo. Confirme en campo.",
    "bp.rb.rail.dims_fabricated": "Dims ELIMINADAS — la cita no aparece en los planos en ninguna orientación: {text}. Cada path lleva la etiqueta [strong] (OCR denso + sin coincidencia — fabricación real) o [weak] (OCR pobre — la falta puede ser culpa del OCR, no del modelo). Descartadas en cualquier caso.",
    "bp.rb.rail.dims_misread": "Dims ELIMINADAS como MISREADS — la cita no aparece en la página pero un texto OCR real está a una sola edición de carácter: {text}. Diagnosticado como error de transcripción, no invención. Valor anulado para el costo; la cadena real impresa queda nombrada para el campo.",
    "bp.rb.rail.dims_shared_source": "Dim(s) que comparten evidencia con otro path — {text}. Una cita impresa compartida es una dimensión REAL y localizada, y A MENUDO es correcta (58'-0 total es genuinamente el ancho frontal y el trasero), así que se MUESTRA aquí con todos sus consumidores nombrados. ENMENDADO send-127: cuando una misma cita la reclaman dos fachadas para el mismo campo, la pertenencia no está establecida y la cifra ya no alimenta ninguna cantidad — solo visualización.",
    "bp.rb.rail.attribution_corroboration": "CORROBORACIÓN — {text}. Un ancho localizado pero sin atribución puede alimentar una cantidad solo si una segunda lectura independiente del trazado lo confirma: RESUELTA en el propio plano de esa fachada, con cifra de muro (nunca silueta), sin aviso de margen de cerca, con escala limpia y una diferencia dentro del piso de ruido de alzado registrado del 3,8%. La cifra IMPRESA alimenta la cantidad — la lectura dibujada confirma, nunca mide. La diferencia se imprime en ambos casos. La corroboración sirve solo para anchos: una altura nunca puede corroborarse así, porque la altura es su propia regla.",
    "bp.rb.rail.dims_unattributed_quantity_refused": "SIN ATRIBUCIÓN — {text}. Una misma cifra impresa la reclaman varias fachadas, así que no está establecido a cuál pertenece. Evidencia-y-atribución-o-nulo: la cifra se muestra aquí y se RECHAZA en toda cantidad — área de muro y hastial, arranque, perímetro, aleros, faldones, esquinas. Establezca el dueño antes de valorar.",
    "bp.rb.rail.dims_shared_source_conflict": "CONFLICTO DE ATRIBUCIÓN — {text}. Una cita alimenta el MISMO campo en dos features nombradas distintas (dos anchos de pared, por ejemplo). El valor se conserva — no podemos saber cuál consumidor está mal, y la regla vigente es REPORTAR el desacuerdo en lugar de resolverlo (send-13).",
    "bp.rb.consistency.openings_exceed_wall_width": "Las aberturas de la pared {wall} suman {openings_sum_ft:g} ft — {excess_ft:g} ft más del ancho impreso {wall_width_ft:g} ft. No caben ({openings}). Verifique la atribución de pared.",
    "bp.rb.rail.fascia_printed": "Ancho de fascia leído de los planos: {text}\".",
    "bp.rb.rail.fascia_default": "Ancho de fascia SIN acotar en los planos — la app mantiene su valor por defecto. Verifique en campo antes de pedir bobina.",
    "bp.rb.rail.interior_doors_dropped": "{text} puerta(s) interior(es) del plano quedaron FUERA del conteo exterior — las puertas alimentan J-channel y bobina.",
    "bp.rb.rail.count_column_governed": "La columna de CANTIDAD impresa gobierna — las cantidades llevadas se reemplazaron por las celdas del propio cuadro: {text}. Los símbolos del plano nunca se cuentan.",
    "bp.rb.rail.count_cells_unread": "El cuadro imprime cantidades, pero no se leyó celda de CANTIDAD para la(s) marca(s) {text} — no aportan NADA hasta que un humano lea la celda. Señalado, nunca estimado.",
    "bp.rb.rail.count_cell_conflict": "Una marca, una hoja, dos celdas de CANTIDAD distintas: {text}. El cuadro no puede decir ambas — un humano lo resuelve contra la hoja.",
    "bp.rb.rail.count_column_absent": "No se leyó columna de CANTIDAD impresa en el cuadro de ventanas — las cantidades no llevan fuente impresa y no están verificadas.",
    "bp.rb.rail.mark_rows_merged": "Filas de cuadro por hoja unidas en una sola marca, cantidades sumadas entre hojas: {text}. Una fila por hoja no es una marca distinta.",
    "bp.rb.rail.mark_not_located": "Una lectura de texto por máquina no encuentra el impreso de estas filas del cuadro en ninguna de sus hojas (todas las rotaciones revisadas): {text}. Ninguna cita se ubica — la fila fue fabricada y se ELIMINA de la lectura.",
    "bp.rb.rail.mark_quote_miss": "Una lectura de texto por máquina no encuentra un impreso citado en una fila del cuadro — {text}. La cita de tamaño se anula; su parseo nunca llega al despiece. Revise la hoja.",
    "bp.rb.rail.schedule_row_omitted": "La hoja imprime filas del cuadro que la lectura nunca llevó — {text}. Una fila faltante es silenciosa e invisible; este es el instrumento de omisión disparando. Revise el cuadro y agregue lo que contiene.",
    "bp.rb.rail.interior_signal_machine": "Una lectura por máquina de la columna de código dice que estas filas de puerta son INTERIORES — {text}. Eliminadas sin importar la etiqueta de la lectura; HOLLOW CORE no puede llevar etiqueta exterior.",
    "bp.rb.rail.skeleton_match": "Ubicado por el esqueleto de pulgadas enteras — {text}. La máquina no puede leer las fracciones apiladas; los dígitos enteros coincidieron con la hoja. Las fracciones descansan en la transcripción de la lectura.",
    "bp.rb.rail.dim_located_by_skeleton": "Una dimensión de muro ubicada por su esqueleto sin fracciones — {text}. La máquina no pudo leer la fracción apilada, así que los dígitos enteros coincidieron con la hoja; las fracciones descansan en la transcripción de la lectura. Un esqueleto ambiguo o compartido nunca ubica.",
    "bp.rb.rail.mark_merge_suspected": "Dos marcas comparten un código de producto — {text}. Un cuadro real nunca imprime eso: la lectura fusionó filas hermanas (la familia Marca B/C/D). Los dígitos finales son la identidad de la fila — revise ambas filas contra la hoja.",
    "bp.rb.rail.callout_omitted": "Las elevaciones imprimen un perfil de revestimiento que la lectura nunca llevó — {text}. Un perfil faltante es una omisión silenciosa en el despiece; revise las llamadas de la elevación y agregue el acento.",
    "build.stale.msg": "Esta página corre una compilación más vieja que el servidor (página {client} · servidor {server}). Lo que ve puede discrepar en silencio con el backend hasta refrescar.",
    "build.stale.btn": "Refrescar ahora",
    "server.waking.msg": "El servidor se está reiniciando — reconectando y reintentando su última solicitud. Nada de lo mostrado abajo es confiable hasta que este aviso desaparezca.",
    "pe.empty": "Esta medición no lleva desglose por elevación — los acentos de perfil cuelgan de las elevaciones. Las lecturas de foto y plano lo llevan; esta no.",
    "bp.rb.rail.soffit_finish_printed": "Acabado de soffit indicado en los planos: {text}. Ajuste el selector de soffit para coincidir — no es un valor por defecto.",
    "bp.rb.rail.soffit_finish_default": "No se capturó anotación de acabado de soffit — ventilado-vs-sólido está SIN LEER. Revise los alzados antes de ajustar la división de soffit.",
    "bp.rb.rail.overhang_varies": "El alero varía por ubicación en esta casa — un solo número no puede cubrirla: {text}",
    "bp.rb.rail.dims_nulled_no_evidence": "Descartadas por construcción — estas dimensiones llegaron SIN cita de texto impreso y se anularon (evidencia-o-nulo, regla 2026-08-08): {text}. El plano puede tenerlas; la lectura no lo probó.",
    "bp.rb.stability": "¿Coincidieron dos lecturas?",
    "bp.rb.stability.agreed": "DOS LECTURAS COINCIDIERON — estabilidad, no es exactitud. La coincidencia significa que la lectura se repite; solo una medición con cinta o una verificación humana contra la dimensión impresa hace correcto un número.",
    "bp.rb.stability.mismatch": "LAS LECTURAS DISCREPAN — {name}: {a} vs {b}. La lectura no es estable aquí; una persona tiene que resolverlo.",
    "bp.rb.stability.abstained": "Ambas lecturas se abstuvieron en: {text} — establemente sin leer. El plano no las entregó; aún necesitan una persona.",
    "bp.rb.stability.notComparable": "Comparación por dimensión no disponible — la lectura anterior es previa al registro de evidencia. Los conteos y totales de arriba sí se comparan; la próxima relectura comparará dimensión por dimensión.",
    "bp.va.title": "Auditoría visual — de dónde salió cada número",
    "bp.va.approx": "UBICACIÓN APROXIMADA — caja de lectura visual sobre un escaneo; el resaltado es la estimación del modelo de dónde está el impreso, no una medición.",
    "bp.va.exact": "EXACTA — localizada en la capa de texto del PDF.",
    "bp.va.noloc": "Sin ubicación devuelta — la cita impresa es el único ancla.",
    "bp.va.calc": "Aritmética: {text}",
    "bp.va.page": "Hoja {n}",
    "bp.va.dropped": "SIN FUENTE — llegó sin cita impresa y fue descartado por construcción: {text}",
    "bp.va.unread": "Sin leer — ninguna dimensión impresa lo resolvió: {text}",
    "bp.va.none": "No hay dimensiones con evidencia en esta lectura.",
    "bp.rb.rail.no_planes": "Sin planos de techo en esta lectura — las líneas dependientes de planos caen al rectángulo de cuatro paredes",
    "hov.cancel": "Cancelar",
    "hov.agree": "Acepto",
    "hov.qtyVerifyTitle": "Verificación de cantidades requerida",
    "hov.qtyVerifyBody": "Usted es responsable de verificar todas las cantidades antes de enviar este reporte.",
    "hov.openingsTitle": "Aberturas de ventana Vero — Estilo estimado",
    "hov.openingsTap": "Aberturas de ventana ({n}) — toque para revisar estilos",
    "hov.openingsExpanded": "({n} abertura(s) · edite cualquier estilo antes de aplicar)",
    "hov.needsReview": "{n} por revisar",
    "hov.sanity": "Chequeo de consistencia · {n} aviso(s)",
    "hov.tapToReview": "— toque para revisar",
    "sr.integralJ.title": "J-channel de ventanas",
    "sr.integralJ.label": "Las ventanas traen J-channel integral",
    "sr.integralJ.note": "Sí = sin J de ventana aparte, sin sellador de ventana, sin envoltura de ventana, sin capping — cada línea afectada imprime su propia nota.",
    "sr.wallHeights.title": "Alturas de pared B&B (con cinta) — etapa de PEDIDO, no se necesita para cotizar",
    "sr.wallHeights.taped": "Medido — {n} pies en total alimentan el término de altura de listones.",
    "sr.wallHeights.reopen": "Reabrir",
    "sr.wallHeights.placeholder": "alturas en pies, ej. 9, 9, 18.5, 9",
    "sr.wallHeights.tapeIt": "Registrar",
    "sr.wallHeights.note": "La cotización vende con la altura derivada (HOVER-SCHEDULE) — ingrese las alturas medidas en la casa antes del pedido de material. Lo medido reemplaza lo derivado, reversible; los listones se re-derivan en vivo",
    "sr.wasteBaked": "Incluido en la cantidad de línea al importar — cambie el % para recalcular",
    "sr.recomputeWaste": "Recalcular desperdicio en líneas existentes",
    "sr.soffitType": "Tipo de plafón LP",
    "sr.soffit.mix": "Mixto — Ventilado en aleros, Cerrado en verteaguas (predeterminado)",
    "sr.soffit.vented": "Ventilado — todo el plafón como Ventilado (38 Series Vented)",
    "sr.soffit.closed": "Cerrado — todo el plafón como Cerrado (38 Series Closed)",
    "sr.soffit.note": "Se aplica al importar HOVER / Planos — junta o divide las dos líneas de plafón automáticamente",
    "sr.tradeSpecs": "Especificaciones de obra",
    "sr.rederive": "Re-derivar lista de materiales",
    "sr.fasciaWidth440": "Ancho de fascia — 440 Series",
    "sr.fasciaWidth": "Ancho de fascia",
    "sr.default": "(predeterminado)",
    "sr.fascia.noteLp": "Decisión del contratista — sin derivación. Elige el ancho de la tabla 440; la lista de materiales imprime el ancho en la línea. Se re-deriva en vivo.",
    "sr.fascia.noteCoil": "Gobierna la cobertura del rollo .019: ≤10\" el rollo de 24\" se corta a la mitad para 100 LF/rollo; sobre 10\" un rollo cubre 50 LF. Se re-deriva en vivo.",
    "sr.battenSpacing": "Separación de listones",
    "sr.batten.note": "Cada junta de 48\" cae sobre un listón. La nota de línea 190 Series nombra la separación y la diferencia de piezas vs el predeterminado de 12\" cuando cambia.",
    "sr.panelSize": "Tamaño de panel — 38 Series B&B",
    "sr.panel.opt410": "4' × 10' (predeterminado, 40 ft²)",
    "sr.panel.opt48": "4' × 8' (32 ft²)",
    "sr.panel.note": "Cambia el CONTEO de paneles y el SKU — la cobertura 4×8 es 32 ft². Se re-deriva en vivo.",
    "sr.wrapWidth": "Ancho de moldura de envoltura — 540 Series",
    "sr.wrap.note": "Cambia SOLO el nombre del SKU 540 (envoltura + ISC + friso) — los conteos siguen en piezas enteras ÷16. Se re-deriva en vivo.",
    "sr.shakeReveal": "Exposición del shake",
    "sr.shake.note": "Limitado 7\"–10\", predeterminado 7\" — instrucciones de instalación LP: \"540 Series Trim is recommended when the shake reveal selected ranges between a maximum of 10 inches to a minimum of 7 inches\". Cobertura = 4' × exposición ÷ 12 (panel máx 9-7/8\"); se aplica encima el 15% de desperdicio sellado.",
    "sr.in": "pulg",
    "sr.sellMargin": "Venta = Base ÷ (1 − {pct}%) =",
    "sr.sellMarkup": "Venta = Base × (1+{pct}%) =",
    "fv.title": "Verificación en campo — números leídos por IA · confírmelos con su cinta",
    "fv.chip.taped": "MEDIDO ×{n}",
    "fv.chip.notTaped": "SIN MEDIR — LECTURA IA",
    "fv.door.blueprint": "Ver planos de origen →",
    "fv.door.photo": "Ver fotos de origen →",
    "fv.door.hover": "Ver informe Hover →",
    "fv.printAll": "Imprimir todo",
    "fv.sheetsEmpty": "Hojas de elevación — sin corrida completada con paredes",
    "fv.perWall": "Despiece por pared",
    "fv.perWall.sub": "· leído por IA, calculado en el servidor — fuente única",
    "fv.th.wall": "Pared",
    "fv.th.body": "Cuerpo ft²",
    "fv.th.gable": "Gablete ft²",
    "fv.th.dormer": "Buhardilla ft²",
    "fv.th.total": "Total ft²",
    "fv.wall.front": "frente",
    "fv.wall.back": "atrás",
    "fv.wall.left": "izquierda",
    "fv.wall.right": "derecha",
    "fv.gables.title": "Gabletes del contratista — medidos en foto, anclados a escala",
    "fv.gables.sub": "· factor de desperdicio propio asignable · no se inyecta automáticamente al estimado",
    "fv.dormers.title": "Buhardillas del contratista — medidas en foto, ancladas a escala",
    "fv.noScale": "marcado — sin referencia de escala en la foto",
    "fv.frontFace": "Cara frontal",
    "fv.cheeks": "+ mejillas",
    "fv.defaultDepth": "(profundidad predeterminada)",
    "fv.total": "Total",
    "fv.depthFt": "prof. (pies)",
    "fv.tapedDims.title": "Dimensiones de apéndices — mida para reemplazar",
    "fv.tapedDims.sub": "· las dimensiones medidas por el usuario re-derivan el cálculo; las supuestas nunca entran",
    "fv.appendage": "Apéndice",
    "fv.wallRow": "{kind} — pared {wall}",
    "fv.depth": "Profundidad",
    "fv.height": "Altura",
    "fv.printedOnPlans": "{v} pies — impreso en los planos",
    "fv.assumedDepth": "~{v} pies — supuesto, no medido",
    "fv.assumedHeight": "sobre la línea del techo — supuesto, no medido",
    "lpp.runLine": "corrida {id} · {n} líneas",
    "lpp.source": "origen: {label}",
    "lpp.waste": "desperdicio: {n}% — campo Desperdicio %",
    "lpp.geometry": "geometría: {label}",
    "lpp.defaultProfile": "Perfil predeterminado:",
    "lpp.revert": "Revertir",
    "lpp.flag.closed": "cerrado",
    "lpp.flag.reopen": "reabrir",
    "lpp.flag.save": "Guardar",
    "lpp.flag.fieldVerify": "Verificar en campo",
    "lpp.flag.markVerified": "Marcar verificado",
    "lpp.flag.placeholder": "alturas de pared en pies, ej. 9, 9, 18.5, 9",
    "lpp.compare": "Comparar Lap vs B&B",
    "lpp.hideCompare": "Ocultar comparación",
    "lpp.derivedNote": "{n} línea(s) derivada(s) — cantidades y procedencia viven en las líneas de pestañas abajo · colores en el bloque COLORES DE MATERIAL de Información del Trabajo · precios en las pestañas del estimado.",
    "lpp.pendingCount": "{n} línea(s) sin precio de hoja — escaladas por nombre",
    "lpp.matrixNote": "— la matriz informa, nunca prohíbe; verifique con el distribuidor.",
    "lpp.dimCrossCheck": "Verificación cruzada de dimensiones",
    "lpp.cmp.title": "Comparar perfiles — misma geometría, mismo motor",
    "lpp.cmp.line": "Línea",
    "lpp.cmp.current": "Actual — Lap",
    "lpp.cmp.differ": "{n} línea(s) difieren en cantidad",
    "lpp.cmp.shared": "({n} línea(s) compartida(s) idénticas en ambos)",
    "lpp.cmp.note": "Vista de comparación solamente — cantidades y derivaciones, nada se guarda ni se aplica. Los precios viven en las pestañas del estimado.",
    "mls.loading": "Cargando lista de materiales…",
    "mls.unavailable": "Lista de materiales no disponible",
    "mls.expired": "Este enlace ha expirado.",
    "mls.notFound": "Enlace no encontrado o revocado.",
    "mls.loadError": "No se pudo cargar la lista de materiales. Revise su conexión e intente de nuevo.",
    "mls.updatedTitle": "Lista actualizada disponible.",
    "mls.updatedBody": "Esta página muestra la versión exacta impresa el {date}. El estimado ha cambiado desde entonces — pida a su contratista la impresión más reciente.",
    "mls.requestSent": "Solicitud enviada — su contratista ha sido notificado.",
    "mls.requestBtn": "Solicitar lista actualizada",
    "mls.sending": "Enviando…",
    "mls.requestFailed": "No se pudo enviar — intente de nuevo en un momento.",
    "mls.title": "LISTA DE MATERIALES — LP SMARTSIDE",
    "mls.readOnly": "Solo lectura · congelada tal como se imprimió {date}",
    "mls.th.item": "Artículo",
    "mls.th.color": "Color",
    "mls.th.qty": "Cant",
    "mls.th.unit": "Unidad",
    "mls.substitutedFrom": "sustituido desde {name} — re-derivado",
    "mls.unpricedNote": "Lista de materiales — cantidades y derivaciones. El precio se proporciona en el estimado mismo.",
    "mls.geometryFrozen": "Geometría: {label} · fuente única",
    "mls.derivedFrom": "Derivado de mediciones IA confirmadas — corrida {id} · fuente única",
    "mls.backToEstimate": "← Volver al estimado",
    "ars.loading": "Cargando informe de precisión…",
    "ars.unavailable": "Informe de precisión no disponible",
    "ars.title": "Informe de precisión de medición IA",
    "ars.frozen": "Solo lectura · congelado {date}",
    "ars.newer": "Existen corridas calificadas más recientes desde que se congeló este informe. Esta página muestra el informe exacto que se compartió — pida al contratista un enlace nuevo para ver el más reciente.",
    "ars.frozenBodyNote": "El informe congelado a continuación se muestra exactamente como fue sellado (documento original en inglés).",

    "accept.loading": "Cargando su estimado…",
    "accept.notFound.title": "Estimado No Encontrado",
    "accept.notFound.expired": "Este enlace es inválido o ha expirado.",
    "accept.notFound.generic": "No se pudo cargar el estimado.",
    "accept.success.heading": "Listo, {name}.",
    "accept.success.body": "Hemos avisado a {company} que aceptó el estimado {number} por {amount}.",
    "accept.success.next": "Le contactarán dentro de un día hábil para coordinar los próximos pasos.",
    "accept.eyebrow": "Confirmar Aceptación",
    "accept.heading": "Aceptar Estimado {number}",
    "accept.from": "De",
    "accept.totalPrice": "Precio Total",
    "accept.preparedFor": "Preparado para:",
    "accept.model3d.title": "Su Casa — 3D Interactivo",
    "accept.model3d.note": "Construido a partir de mediciones fotográficas con IA de su casa — las mismas mediciones con las que se calculó esta cotización.",
    "accept.model3d.hint": "Arrastre para rotar · desplace para acercar.",
    "accept.model3d.attestation": "{count} ubicación(es) confirmada(s) en campo · {initials} · {date}",
    "accept.model3d.simplifiedTitle": "Su Casa — Boceto 3D Simplificado",
    "accept.model3d.fitNote": "Representación simplificada — no a escala ni forma real.",
    "email.model3dSimplifiedTitle": "Boceto 3D Simplificado",
    "email.model3dFitNote": "Representación simplificada — no a escala ni forma real.",
    "accept.share.title": "Compartir Este Estimado",
    "accept.share.note": "Compártalo con quien necesite verlo — el enlace y el código QR abren esta misma página.",
    "accept.share.copy": "Copiar enlace",
    "accept.share.copied": "Enlace copiado",
    "accept.confirmHint": "Marque la casilla de arriba para habilitar la confirmación.",
    "accept.suppliedBy": "Materiales suministrados por {supplier}",
    "accept.optionalNote": "Nota opcional para el contratista",
    "accept.notePlaceholder": "p. ej. ¿Pueden empezar la próxima semana? O traigan muestras de moldura extra.",
    "accept.consent": "Yo, {name}, acepto este estimado por {amount} cotizado por {company}.",
    "accept.consent.fallback": "el cliente",
    "accept.submit": "Confirmar Aceptación →",
    "accept.submitting": "Enviando aceptación…",
    "accept.disclaimer": "Al aceptar, autoriza a {company} a comenzar la programación. Puede responder al correo original con cualquier cambio.",
    "accept.secureLink": "Enlace seguro de aceptación.",

    "email.estimate": "Estimado",
    "email.validThrough": "Válido hasta {date}",
    "email.preheader": "Estimado {number} — total {amount}. Válido 30 días.",
    "email.preparedFor": "Preparado Para",
    "email.estimator": "Estimador",
    "email.billing": "Facturación",
    "email.scopeOfWork": "Alcance del Trabajo",
    "email.elevationTitle": "Desglose de Revestimiento por Elevación",
    "email.elevationFront": "Frente",
    "email.elevationBack": "Atrás",
    "email.elevationLeft": "Izquierda",
    "email.elevationRight": "Derecha",
    "email.elevationTotal": "Área Total de Revestimiento",
    "email.jobPhotos": "Fotos del Trabajo",
    "email.windowsOpenings": "Ventanas",
    "email.model3d": "Su Casa — Modelo 3D",
    "email.model3dNote": "Construido a partir de mediciones fotográficas con IA de su casa — las mismas mediciones con las que se calculó esta cotización.",
    "email.model3dVerifyNote": "Algunos detalles están sujetos a verificación en el sitio.",
    "email.total": "Precio Total",
    "email.pricePending": "precio por confirmar",
    "email.pendingNote": "Los artículos marcados \"precio por confirmar\" están pendientes de confirmación del proveedor y no se incluyen en el total. Se cotizarán antes de comenzar el trabajo.",
    "email.validityWithDate": "Este estimado es válido hasta {date}. El precio final puede variar según las condiciones del sitio que se descubran después de iniciar el trabajo.",
    "email.validityGeneric": "Válido por 30 días desde la fecha anterior. El precio final puede variar según las condiciones del sitio que se descubran después de iniciar el trabajo.",
    "email.acceptCta": "Aceptar este Estimado  →",
    "email.replyHint": "O responda a este correo con cualquier pregunta.",
    "email.questionsFooter": "¿Preguntas sobre este estimado? Responda a este correo y le contestaremos pronto.",
    "email.materialsBy": "Materiales suministrados por {supplier}",
    "email.poweredBy": "Impulsado por Pro-Quote Estimating Tool",
    "email.materialsExcluded": "Materiales excluidos",
    "email.porchCeilingsIncluded": "Incluye techo{plural} de porche",
    "email.sentBy": "Este estimado fue enviado por {company}.",
    "email.subjectWithCustomer": "Su estimado de revestimiento{num} de {company} — {customer}",
    "email.subjectGeneric": "Su estimado de revestimiento{num} de {company}",
    "email.greeting": "Hola {first},\n\nGracias por la oportunidad de cotizar su proyecto — el estimado detallado está abajo. Échele un vistazo y responda con cualquier pregunta; con gusto le explico cualquier cosa que no quede clara.\n\n— {who}",
    "email.greetingFallbackName": "buenos días",
    "email.greetingFallbackWho": "Su contratista",

    // Iter 38–40: window opening panels + snapshot cards
    "win.addOpening": "Agregar abertura",
    "win.noOpenings": "Aún no hay aberturas. Pulse <strong>Agregar abertura</strong> para comenzar.",
    "win.width": "Ancho",
    "win.height": "Alto",
    "win.qty": "Cant.",
    "win.model": "Modelo",
    "win.ui": "PU",
    "win.base": "Base",
    "win.total": "Total",
    "win.outOfRange": "fuera de rango",
    "win.options": "Opciones de ventana",
    "win.optionsPerWindow": "+{amt}/ventana",
    "win.perWindow": "Por ventana:",
    "win.openingsLabel": "{n} abertura · escriba A × A abajo para cotizar cada ventana",
    "win.openingsLabelPlural": "{n} aberturas · escriba A × A abajo para cotizar cada ventana",
    "win.openingsLabelFixed": "{n} abertura · elija modelo y color hermano",
    "win.openingsLabelFixedPlural": "{n} aberturas · elija modelo y color hermano",
    "win.sisterColor": "Color hermano (define el base)",
    "win.glassPackage": "Paquete de vidrio",
    "win.tempered": "Recargo por templado",
    "win.premiumOptions": "Opciones premium",
    "win.premiumOptionsHint": "selección múltiple; el precio depende del rango PU",
    "win.notesPlaceholder": "Cocina — pared oeste (etiqueta o nota opcional)",
    "win.addNote": "Agregar nota",
    "win.removeOpening": "Quitar abertura",
    "win.hideNotes": "Ocultar notas",
    "win.optional": "opcional",
    "win.none": "— Ninguno —",
    "win.upgradeOptions": "Opciones de mejora",
    "win.snapshot": "Resumen del trabajo",
    "win.snapshot.subtitle": "aberturas · mano de obra de instalación y moldura · rollos de material",
    "win.snapshot.baseTotal": "Total base",
    "win.snapshot.empty": "Agregue una abertura arriba o escriba cantidades en cualquier renglón de instalación o moldura — los totales aparecerán aquí en tiempo real.",
    "win.snapshot.openings": "Aberturas",
    "win.snapshot.windowsCount": "{n} ventana",
    "win.snapshot.windowsCountPlural": "{n} ventanas",
    "win.snapshot.installTrim": "Instalación + moldura",
    "win.snapshot.installSub": "{n} renglón · mano de obra",
    "win.snapshot.installSubPlural": "{n} renglones · mano de obra",
    "win.snapshot.material": "Lista de materiales",
    "win.snapshot.materialSub": "{n} renglón de rollo",
    "win.snapshot.materialSubPlural": "{n} renglones de rollo",
    "win.mezzo.tempered.label": "Templado completo",
    "win.mezzo.tempered.fullHint": "$9.18/pie² × {sqft} pies²",
    "win.colors.veroDesc": "base de vinil + acabado laminado o pintado",
    "win.colors.mezzoDesc": "sólido extruido · FrameWorks · veteado",
    "win.color.exterior": "Color exterior",
    "win.color.interior": "Color interior",
    "win.color.select": "— Seleccione —",
    "win.color.laminateWarn": "<strong>Atención:</strong> los laminados Vero solo se fabrican sobre base de vinil <strong>blanca</strong>. Combine el laminado con un color hermano de base blanca, o cambie el lado laminado a un acabado pintado.",
    "win.color.laminateNotice": "Laminado seleccionado — la fabricación usará una base de vinil <strong>blanca</strong>.",

    "win.bulkApply.title": "¿Aplicar a todas las ventanas?",
    "win.bulkApply.body": "¿Aplicar “{option}” a las otras {count} ventanas cargadas también?",
    "win.bulkApply.hint": "Solo se actualizarán las ventanas cuyo tipo de producto admita esta opción. Puede ajustar ventanas individuales después.",
    "win.bulkApply.skip": "Solo esta",
    "win.bulkApply.applyAll": "Aplicar a las {count}",

    "tabPicker.title.quote": "Imprimir Cotización al Cliente",
    "tabPicker.title.material": "Imprimir Lista de Materiales",
    "tabPicker.helper.quote": "Elija qué líneas de producto enviar al cliente. Un PDF por selección mantiene la cotización limpia cuando el trabajo abarca varias líneas.",
    "tabPicker.helper.material": "Elija qué líneas de producto imprimir para el proveedor. Cada selección genera su propia lista para que el almacén separe materiales por línea.",
    "tabPicker.cancel": "Cancelar",
    "tabPicker.continue": "Continuar · {n}",
    "tabPicker.label.vinyl": "Vinil",
    "tabPicker.label.ascend": "Revestimiento Ascend",
    "tabPicker.label.lp_smart": "LP SmartSide",
    "tabPicker.label.windows": "Ventanas",
    "tabLabel.vinyl": "Vinil",
    "tabLabel.ascend": "Ascend",
    "tabLabel.lpSmart": "LP Smart",
    "tabLabel.windows": "Ventanas",
    "tabLabel.mezzo": "Mezzo",

    // --- Photo fill-in boxes (Three Doors step 6 + gate, ruled 2026-08-02) ---
    "pf.title": "Datos de foto — lo que las fotos no pueden ver",
    "pf.hint": "Solo la puerta de foto. La casilla llena el hueco que dejan las fotos — nunca reemplaza un valor medido. Una casilla SIN DEFINIR BLOQUEA la cotización (alcance sin definir — nunca $0). Escriba 0 si la casa de verdad no tiene. Se recalcula en vivo.",
    "pf.soffit": "Plafón",
    "pf.dripEdge": "Goterón",
    "pf.totalTrim": "Moldura total",
    "pf.notSet": "Sin definir — bloquea la cotización",
    "pf.friezeQ": "¿Tabla de friso?",
    "pf.yes": "Sí",
    "pf.no": "No",
    "pf.friezeHint": "Solo Sí/No — los LF del friso se derivan de las medidas (nivel = aleros {eaves} LF · inclinado = limatesas {rakes} LF). Nunca vuelve a escribir un número que el sistema ya tiene.",

    // --- Photo fill-in gate banner ---
    "gate.pf.title": "Los datos de foto bloquean esta cotización",
    "gate.pf.body": "DATOS DE FOTO SIN DEFINIR — {count} casilla(s) abiertas: {boxes}. ALCANCE SIN DEFINIR, nunca $0 — las fotos no pueden ver esto; escriba el valor (0 es una decisión) o responda Sí/No al friso antes de que esta cotización llegue a un cliente.",
    "gate.pf.hint": "Llene las casillas en Especificaciones de instalación abajo — el correo, el PDF y la cotización siguen bloqueados hasta definir el alcance.",
    "gate.pf.box.soffit": "plafón ft²",
    "gate.pf.box.dripEdge": "goterón LF",
    "gate.pf.box.totalTrim": "moldura total ft²",
    "gate.pf.box.frieze": "friso sí/no",

    // --- Derived color-tier chips (tier terms Standard/Architectural stay
    // verbatim — they are catalog price terms that appear inside SKU names) ---
    "tier.architectural": "Nivel Architectural — así se cotiza",
    "tier.standard": "Nivel Standard",
    "pf.history": "Historial de datos",
    "pf.lastSet": "Definido por {by} · {date}",
    "pf.historyEmpty": "Aún no hay cambios registrados",
  },
};
